import { Settings, Save, Lock, ExternalLink, Building2, Image, Loader2, Upload, Languages, UserCheck, MessageSquare, Bell, Plus, Trash2, GraduationCap, RefreshCcw, CheckCircle2, Bot, Radio, Volume2, CreditCard, KeyRound, Phone } from 'lucide-react';
import DepartmentTreeSelect from '../e-education/components/selectors/DepartmentTreeSelect';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import TabPage, { TabItemProps } from '../../components/dynamic/Tab';
import checkPermission from '../../utils/permission';
import { Select, SelectItem } from '@heroui/select';
import { ApiService } from '../../services/api';
import type { CoachCallSettings } from '../../services/api';
import type { ObserveCallRow } from '../../types/navigator';
import { ARAMA_DURUM, SONUC_AD, ARAMA_TUR, SMS_AD, MEMNUNIYET_AD } from '../coach-observe/aramaEtiketleri';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../hooks/useModal';
import DepartmentSelector from '../../components/selectors/DepartmentSelector';
import { isYesToolsHostname } from '../../utils/yesTools';
import { defaultMenuItems } from '../../sidebar/data/defaultMenuItems';
import { getPaymentProviderMeta } from '../../utils/paymentProviders';
import PaymentPackagesEditor, { toEditablePackages, toPackagePayload } from './PaymentPackagesEditor';
import PaymentExamDaysEditor from './PaymentExamDaysEditor';

const SUPPORTED_LANGUAGES = [
  { code: 'tr-TR', name: 'Türkçe', countryCode: 'tr' },
  { code: 'en-US', name: 'English', countryCode: 'us' },
];

const getFlagUrl = (countryCode: string) => `https://flagcdn.com/w40/${countryCode}.png`;

const faviconFiles = [
  'apple-touch-icon.png',
  'favicon-96x96.png',
  'favicon.ico',
  'favicon.svg',
  'web-app-manifest-192x192.png',
  'web-app-manifest-512x512.png'
];

interface AiTeacherAudioModel {
  id: string;
  label: string;
  mode: 'tts' | 'live' | 'browser' | 'none';
  description?: string;
}

const AI_TEACHER_AUDIO_MODELS: AiTeacherAudioModel[] = [
  {
    id: 'gemini-2.5-flash-preview-tts',
    label: 'Gemini 2.5 Flash TTS (ekonomik)',
    mode: 'tts',
    description: 'Metin yanıtını sese çevirir; 3.1 TTS ile aynı iş, yaklaşık yarı maliyet.',
  },
  {
    id: 'gemini-3.1-flash-tts-preview',
    label: 'Gemini 3.1 Flash TTS',
    mode: 'tts',
    description: 'Metin yanıtını yüksek kaliteli ses dosyasına çevirir.',
  },
  {
    id: 'gemini-3.8-live',
    label: 'Gemini 3.8 Live (önerilen)',
    mode: 'live',
    description: 'Cevap ve sesi aynı düşük gecikmeli Live API oturumunda üretir; ilk ses ~1-1,5 sn. Öğretmen sesinin dakikası ~0,018 $.',
  },
  {
    id: 'gemini-3.1-flash-live-preview',
    label: 'Gemini 3.1 Flash Live Preview (eski)',
    mode: 'live',
    description: 'Önizleme modeli; Google fiyat listesinden çıktı, kaldırılabilir. Yerine Gemini 3.8 Live kullanın.',
  },
  {
    id: 'browser-tts',
    label: 'Tarayıcı Sesi (ücretsiz)',
    mode: 'browser',
    description: 'Seslendirme öğrencinin cihazında yapılır; API maliyeti sıfırdır. Ses kalitesi cihaza göre değişir.',
  },
  {
    id: 'text-only',
    label: 'Sessiz (sadece yazılı sohbet)',
    mode: 'none',
    description: 'AI öğretmen hiç seslendirme yapmaz; öğrenci yalnızca yazışır. Ses maliyeti oluşmaz.',
  },
];

/** NewUser.tsx ile uyumlu form kabuğu; her sekmede accent farklı */
type SettingsFormAccent =
  | 'emerald'
  | 'violet'
  | 'cyan'
  | 'amber'
  | 'orange'
  | 'indigo'
  | 'rose'
  | 'sky'
  | 'fuchsia';

const SETTINGS_FORM_PAGE = 'h-full overflow-y-auto bg-gray-100 p-2 sm:p-2';
const SETTINGS_PAGE_INNER = 'pb-10 max-w-6xl mx-auto';
const SETTINGS_SHELL = 'bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6';
const SETTINGS_SECTION = 'rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm';
const SETTINGS_PAGE_TITLE_CLASS = 'text-[19px] sm:text-[23px] font-semibold text-gray-900 flex items-center gap-2';

const SETTINGS_INPUT_BASE =
  'w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400';

const settingsAccent: Record<
  SettingsFormAccent,
  {
    badge: string;
    underline: string;
    field: string;
    primaryBtn: string;
  }
> = {
  emerald: {
    badge: 'inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700',
    underline: 'bg-emerald-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-sm',
  },
  violet: {
    badge: 'inline-flex items-center rounded-full border border-violet-100 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700',
    underline: 'bg-violet-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-violet-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-violet-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 shadow-sm',
  },
  cyan: {
    badge: 'inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700',
    underline: 'bg-cyan-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-cyan-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-cyan-600 to-teal-600 text-white hover:from-cyan-700 hover:to-teal-700 shadow-sm',
  },
  amber: {
    badge: 'inline-flex items-center rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800',
    underline: 'bg-amber-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-amber-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700 shadow-sm',
  },
  orange: {
    badge: 'inline-flex items-center rounded-full border border-orange-100 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-800',
    underline: 'bg-orange-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-orange-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:from-orange-700 hover:to-amber-700 shadow-sm',
  },
  indigo: {
    badge: 'inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700',
    underline: 'bg-indigo-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 shadow-sm',
  },
  rose: {
    badge: 'inline-flex items-center rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700',
    underline: 'bg-rose-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-rose-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-rose-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-700 hover:to-pink-700 shadow-sm',
  },
  sky: {
    badge: 'inline-flex items-center rounded-full border border-sky-100 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800',
    underline: 'bg-sky-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-sky-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-sky-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 shadow-sm',
  },
  fuchsia: {
    badge: 'inline-flex items-center rounded-full border border-fuchsia-100 bg-fuchsia-50 px-2.5 py-1 text-xs font-semibold text-fuchsia-700',
    underline: 'bg-fuchsia-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-fuchsia-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-fuchsia-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white hover:from-fuchsia-700 hover:to-pink-700 shadow-sm',
  },
};

function settingsInputClass(accent: SettingsFormAccent) {
  return `${SETTINGS_INPUT_BASE} ${settingsAccent[accent].field}`;
}

interface SettingsData {
  id: string;
  companyName: string;
  companyLogoId: string | null;
  defaultLanguage?: string;
  media?: {
    id: string;
    url: string;
    name: string;
    type: string;
  } | null;

  faviconAppleTouchIcon?: string | null;
  favicon96x96?: string | null;
  faviconIco?: string | null;
  faviconSvg?: string | null;
  siteWebmanifest?: string | null;
  faviconManifest192?: string | null;
  faviconManifest512?: string | null;
  n8nLatexExtractorWebhookUrl?: string | null;
  announcementCount?: number;
}

interface ExamDateItem {
  id: string;
  name: string;
  date: string;
  departmentIds: string[];
  allDepartments: boolean;
}

interface ExamDepartmentNode {
  id: string;
  name: string;
  parentId?: string | null;
}

const FAVICON_FIELD_MAP: Record<string, keyof SettingsData> = {
  'apple-touch-icon.png': 'faviconAppleTouchIcon',
  'favicon-96x96.png': 'favicon96x96',
  'favicon.ico': 'faviconIco',
  'favicon.svg': 'faviconSvg',
  'site.webmanifest': 'siteWebmanifest',
  'web-app-manifest-192x192.png': 'faviconManifest192',
  'web-app-manifest-512x512.png': 'faviconManifest512',
};

const useSystemSettings = () => {
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null);
  const [fetching, setFetching] = useState(true);
  const { showToast } = useModal();

  const fetchSettings = useCallback(async () => {
    try {
      setFetching(true);
      const response = await ApiService.getApi('settings');
      if (response) {
        setSettingsData(response);
      }
    } catch (error) {
      console.error('Ayarlar yüklenirken hata:', error);
      showToast('Ayarlar yüklenirken bir hata oluştu', 'error');
    } finally {
      setFetching(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settingsData, setSettingsData, fetching, fetchSettings };
};

const LoadingState = () => (
  <div className='h-full flex items-center justify-center p-6'>
    <div className='flex flex-col items-center gap-3'>
      <Loader2 className='w-8 h-8 animate-spin text-blue-600' />
      <p className='text-gray-600 text-sm'>Ayarlar yükleniyor...</p>
    </div>
  </div>
);

function GeneralSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const { settingsData, fetching, setSettingsData } = useSystemSettings();

  const [companyName, setCompanyName] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('tr-TR');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (settingsData) {
      setCompanyName(settingsData.companyName || '');
      setDefaultLanguage(settingsData.defaultLanguage || 'tr-TR');
      if (settingsData.media?.url) setLogoPreview(settingsData.media.url);
    }
  }, [settingsData]);

  const handleLogoChange = (file: File | null) => {
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('companyName', companyName);
      formData.append('defaultLanguage', defaultLanguage);

      if (logoFile) formData.append('companyLogo', logoFile);

      const response = await ApiService.updateSettings(formData);

      if (!response) throw new Error('Kayıt başarısız');

      setSettingsData(response);
      if (response.media?.url) setLogoPreview(response.media.url);

      setLogoFile(null);
      showToast('Sistem ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Ayarlar güncellenirken hata:', error);
      showToast(error?.message || 'Ayarlar güncellenirken bir hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <LoadingState />;

  const accent: SettingsFormAccent = 'emerald';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Settings className='w-6 h-6 text-slate-600' />
              Genel Bilgiler
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Şirket adı ve logo gibi temel ayarları buradan yönetebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div className={SETTINGS_SECTION}>
              <div className='mb-3'>
                <span className={a.badge}>Kurum bilgileri</span>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Building2 className='w-4 h-4 text-emerald-600' />
                    Şirket / Kurum Adı
                  </label>
                  <input
                    type='text'
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={settingsInputClass(accent)}
                    placeholder='Sistem Adını Giriniz'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Image className='w-4 h-4 text-emerald-600' />
                    Sistem Logosu
                  </label>
                  <div className='flex items-center gap-3'>
                    {logoPreview && (
                      <div className='w-12 h-12 flex-shrink-0 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm'>
                        <img
                          src={logoPreview}
                          alt='Logo'
                          className='w-full h-full object-contain p-1'
                        />
                      </div>
                    )}

                    <label className={`flex-1 flex items-center justify-center px-3.5 py-2.5 border border-dashed rounded-xl border-emerald-200 bg-gradient-to-b from-emerald-50/80 to-white shadow-sm transition-colors ${hasUpdatePermission ? 'cursor-pointer hover:border-emerald-300 hover:bg-white' : 'cursor-not-allowed opacity-60'}`}>
                      <Upload className='w-4 h-4 mr-2 text-slate-500' />
                      <span className='text-sm text-gray-600 truncate'>
                        {logoFile ? logoFile.name : 'Logo Yükle'}
                      </span>
                      <input
                        type='file'
                        accept='image/*'
                        className='hidden'
                        onChange={(e) => handleLogoChange(e.target.files?.[0] || null)}
                        disabled={!hasUpdatePermission}
                      />
                    </label>

                    {logoPreview && hasUpdatePermission && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Sistem logosunu sıfırlamak istediğinize emin misiniz?')) return;
                          setLoading(true);
                          try {
                            await ApiService.resetCompanyLogo();
                            setLogoPreview(null);
                            setLogoFile(null);
                            showToast('Logo başarıyla sıfırlandı', 'success');
                          } catch (error) {
                            showToast('Logo sıfırlanırken hata oluştu', 'error');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="p-2.5 text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                        title="Logoyu Sıfırla"
                      >
                        <i className="fa fa-trash" />
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Languages className='w-4 h-4 text-emerald-600' />
                    Varsayılan Dil
                  </label>
                  <Select
                    aria-label="Varsayılan Dil"
                    selectedKeys={[defaultLanguage]}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0];
                      if (selected) setDefaultLanguage(selected.toString());
                    }}
                    isDisabled={!hasUpdatePermission}
                    classNames={{
                      trigger: "bg-slate-50 border border-slate-200/90 hover:border-emerald-300 rounded-xl h-11 min-h-11 shadow-sm data-[hover=true]:bg-white",
                      value: "text-slate-800 font-medium text-sm",
                      popoverContent: "bg-white rounded-xl shadow-xl border border-slate-200",
                    }}
                    renderValue={(items) => {
                      const lang = SUPPORTED_LANGUAGES.find(l => l.code === defaultLanguage);
                      return lang ? (
                        <div className="flex items-center gap-3">
                          <img
                            src={getFlagUrl(lang.countryCode)}
                            alt={lang.name}
                            className="w-6 h-4 object-cover rounded-sm shadow-sm"
                          />
                          <span>{lang.name}</span>
                        </div>
                      ) : null;
                    }}
                  >
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <SelectItem
                        key={lang.code}
                        textValue={lang.name}
                        startContent={
                          <img
                            src={getFlagUrl(lang.countryCode)}
                            alt={lang.name}
                            className="w-7 h-5 object-cover rounded-sm shadow-sm"
                          />
                        }
                      >
                        <span className="font-medium">{lang.name}</span>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
              <div className='flex justify-end'>
                <button
                  type='submit'
                  disabled={!hasUpdatePermission || loading}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 transition-all ${a.primaryBtn}`}
                >
                  {loading ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Save className='w-4 h-4' />
                  )}
                  {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function FaviconSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const { settingsData, fetching } = useSystemSettings();

  const [faviconFiles_, setFaviconFiles] = useState<Record<string, File>>({});
  const [faviconLoading, setFaviconLoading] = useState(false);

  const handleFaviconChange = (fileName: string, file: File | null) => {
    if (file) setFaviconFiles(prev => ({ ...prev, [fileName]: file }));
  };

  const handleFaviconUpload = async () => {
    if (Object.keys(faviconFiles_).length === 0) {
      showToast('Lütfen en az bir favicon dosyası seçin', 'warning');
      return;
    }

    setFaviconLoading(true);
    try {
      const formData = new FormData();
      for (const [name, file] of Object.entries(faviconFiles_)) {
        formData.append(name, file);
      }

      await ApiService.uploadFavicons(formData);

      setFaviconFiles({});
      showToast('Favicon dosyaları başarıyla yüklendi', 'success');

      window.location.reload();
    } catch (error: any) {
      console.error('Favicon yüklenirken hata:', error);

      if (error?.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) =>
          err.error || `${err.file}: Boyut hatalı`
        ).join('\n');

        showToast(`Boyut uyumsuzluğu: ${errorMessages}`, 'error');
      } else {
        showToast(error?.message || 'Favicon yüklenirken bir hata oluştu', 'error');
      }
    } finally {
      setFaviconLoading(false);
    }
  };

  if (fetching) return <LoadingState />;

  const accent: SettingsFormAccent = 'violet';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <h1 className='text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2'>
                <Image className='w-6 h-6 text-violet-600' />
                Favicon Dosyaları
              </h1>
              <p className='text-sm text-gray-600 mt-1'>Sitenizin tarayıcı sekmelerinde ve mobil cihazlarda nasıl görüneceğini yönetin.</p>
              <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
            </div>
            <a
              href='https://realfavicongenerator.net'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-violet-700 bg-violet-50 px-3 py-2 rounded-xl border border-violet-200 hover:bg-violet-100 transition-colors shadow-sm'
            >
              <ExternalLink className='w-3.5 h-3.5' />
              Favicon Oluşturucu
            </a>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Dosya yükleme</span>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {faviconFiles.map((fileName) => {
                if (fileName === 'site.webmanifest') return null;
                const faviconField = FAVICON_FIELD_MAP[fileName];
                const faviconUrl = settingsData && faviconField ? (settingsData[faviconField] as string | null) : null;

                return (
                  <div key={fileName}>
                    <label className='block text-sm font-medium text-gray-700 mb-2 truncate' title={fileName}>
                      {fileName}
                    </label>

                    <div className='flex gap-4 items-start'>
                      {(fileName.endsWith('.png') || fileName.endsWith('.ico') || fileName.endsWith('.svg')) && (
                        <div className='w-12 h-12 flex-shrink-0 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm'>
                          {faviconUrl ? (
                            <img
                              src={faviconUrl}
                              alt={fileName}
                              className='w-full h-full object-contain p-1 opacity-90'
                              onError={(e) => e.currentTarget.style.display = 'none'}
                            />
                          ) : (
                            <span className='text-xs text-slate-400'>Yok</span>
                          )}
                        </div>
                      )}

                      <label className={`flex-1 flex items-center justify-center px-3.5 py-3 border border-dashed rounded-xl bg-gradient-to-b from-violet-50/80 to-white border-violet-200 shadow-sm transition-colors min-h-[3rem] ${hasUpdatePermission ? 'cursor-pointer hover:border-violet-300 hover:bg-white' : 'cursor-not-allowed opacity-60'}`}>
                        <span className='text-xs text-gray-600 truncate font-medium'>
                          {faviconFiles_[fileName] ? faviconFiles_[fileName].name : 'Dosya Seç'}
                        </span>
                        <input
                          type='file'
                          className='hidden'
                          onChange={(e) => handleFaviconChange(fileName, e.target.files?.[0] || null)}
                          disabled={!hasUpdatePermission}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className='flex flex-wrap items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-100'>
              <button
                type='button'
                onClick={async () => {
                  if (!window.confirm('Tüm favicon dosyalarını sıfırlamak istediğinize emin misiniz?')) return;
                  setFaviconLoading(true);
                  try {
                    await ApiService.resetFavicons();
                    showToast('Favicon ayarları sıfırlandı', 'success');
                    window.location.reload();
                  } catch (error) {
                    showToast('Favicon sıfırlanırken hata oluştu', 'error');
                  } finally {
                    setFaviconLoading(false);
                  }
                }}
                disabled={!hasUpdatePermission || faviconLoading}
                className='inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium'
              >
                Sıfırla
              </button>

              <button
                type='button'
                onClick={handleFaviconUpload}
                disabled={!hasUpdatePermission || faviconLoading || Object.keys(faviconFiles_).length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {faviconLoading ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Upload className='w-4 h-4' />
                )}
                {faviconLoading ? 'Yükleniyor...' : 'Favicon Dosyalarını Yükle'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Department {
  id: string;
  name: string;
  color?: string;
  parentId?: string | null;
}

interface DepartmentDropdownProps {
  value: string | null;
  onChange: (id: string | null) => void;
  onDepartmentSelect?: (department: { id: string; name: string; color?: string } | null) => void;
  selectedDepartment: { id: string; name: string; color?: string } | null;
  disabled?: boolean;
  placeholder?: string;
}

function DepartmentDropdown({ value, onChange, onDepartmentSelect, selectedDepartment, disabled, placeholder = 'Departman Seçiniz...' }: DepartmentDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const syncedSelectedDepartment =
    selectedDepartment && value && selectedDepartment.id === value
      ? selectedDepartment
      : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (ids: string[]) => {
    const newId = ids.length > 0 ? ids[0] : null;
    onChange(newId);
    setIsOpen(false);
  };

  const handleSelectWithDetails = (department: { id: string; name: string; color?: string } | null) => {
    onDepartmentSelect?.(department);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-left flex items-center justify-between shadow-sm transition-all ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200' : 'hover:border-slate-300 hover:bg-white cursor-pointer'
          } ${isOpen ? 'ring-4 ring-slate-100 border-slate-200 bg-white' : ''}`}
      >
        <span className={syncedSelectedDepartment ? 'text-gray-900' : 'text-gray-500'}>
          {syncedSelectedDepartment ? syncedSelectedDepartment.name : placeholder}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <DepartmentTreeSelect
            value={value ? [value] : []}
            onChange={handleSelect}
            onSelectWithDetails={handleSelectWithDetails}
            disabled={disabled}
            multiple={false}
          />
        </div>
      )}
    </div>
  );
}

function EducationSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teacherDepartmentId, setTeacherDepartmentId] = useState<string | null>(null);
  const [parentDepartmentId, setParentDepartmentId] = useState<string | null>(null);
  const [studentDepartmentId, setStudentDepartmentId] = useState<string | null>(null);
  const [teacherDepartment, setTeacherDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [parentDepartment, setParentDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [studentDepartment, setStudentDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const educationSettings = await ApiService.getEducationSettings();

        setTeacherDepartmentId(educationSettings.teacherDepartmentId);
        setParentDepartmentId(educationSettings.parentDepartmentId);
        setStudentDepartmentId(educationSettings.studentDepartmentId);
        setTeacherDepartment(educationSettings.teacherDepartment);
        setParentDepartment(educationSettings.parentDepartment);
        setStudentDepartment(educationSettings.studentDepartment);
      } catch (error) {
        console.error('Eğitim ayarları yüklenirken hata:', error);
        showToast('Eğitim ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      const result = await ApiService.updateEducationSettings({
        teacherDepartmentId: teacherDepartmentId || null,
        parentDepartmentId: parentDepartmentId || null,
        studentDepartmentId: studentDepartmentId || null,
      });

      const educationSettings = await ApiService.getEducationSettings();
      setTeacherDepartment(educationSettings.teacherDepartment);
      setParentDepartment(educationSettings.parentDepartment);
      setStudentDepartment(educationSettings.studentDepartment);

      showToast('Eğitim ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Eğitim ayarları güncellenirken hata:', error);
      showToast(error?.message || 'Eğitim ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <GraduationCap className='w-6 h-6 text-cyan-600' />
              Eğitim Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Öğretmen, öğrenci ve veli departmanlarını buradan ayarlayabilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div className={SETTINGS_SECTION}>
              <div className='mb-3'>
                <span className={a.badge}>Departman eşlemeleri</span>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-600"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    Öğretmen Departmanı
                  </label>
                  <p className='text-xs text-gray-500 mb-2'>
                    Öğretmen raporlarında bu departman altındaki kullanıcılar listelenir.
                  </p>
                  <DepartmentDropdown
                    value={teacherDepartmentId}
                    onChange={(id) => {
                      setTeacherDepartmentId(id);
                      if (!id) setTeacherDepartment(null);
                    }}
                    onDepartmentSelect={setTeacherDepartment}
                    selectedDepartment={teacherDepartment}
                    disabled={!hasUpdatePermission}
                    placeholder="Öğretmen departmanı seçin..."
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-600"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></svg>
                    Öğrenci Departmanı
                  </label>
                  <p className='text-xs text-gray-500 mb-2'>
                    Öğrenci kullanıcılarının bulunduğu departmanı belirleyin.
                  </p>
                  <DepartmentDropdown
                    value={studentDepartmentId}
                    onChange={(id) => {
                      setStudentDepartmentId(id);
                      if (!id) setStudentDepartment(null);
                    }}
                    onDepartmentSelect={setStudentDepartment}
                    selectedDepartment={studentDepartment}
                    disabled={!hasUpdatePermission}
                    placeholder="Öğrenci departmanı seçin..."
                  />
                  {studentDepartmentId && !studentDepartment && (
                    <p className='mt-2 text-xs text-red-600'>
                      Kayıtlı öğrenci departmanı silinmiş ya da bulunamıyor. Hata kitapçığı, haftalık rutinler ve öğrenci listeleri çalışmaz; lütfen öğrencilerin bulunduğu departmanı yeniden seçip kaydedin.
                    </p>
                  )}
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-600"><path d="M9 7v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><path d="M15 11v4M9 11v4" /><rect width="20" height="14" x="2" y="7" rx="2" /></svg>
                    Veli Departmanı
                  </label>
                  <p className='text-xs text-gray-500 mb-2'>
                    Veli kullanıcılarının bulunduğu departmanı belirleyin.
                  </p>
                  <DepartmentDropdown
                    value={parentDepartmentId}
                    onChange={(id) => {
                      setParentDepartmentId(id);
                      if (!id) setParentDepartment(null);
                    }}
                    onDepartmentSelect={setParentDepartment}
                    selectedDepartment={parentDepartment}
                    disabled={!hasUpdatePermission}
                    placeholder="Veli departmanı seçin..."
                  />
                </div>
              </div>
            </div>

            <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
              <div className='flex justify-end'>
                <button
                  type='submit'
                  disabled={!hasUpdatePermission || saving}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
                >
                  {saving ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Save className='w-4 h-4' />
                  )}
                  {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CafeSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cafeUnregisteredStudentDepartmentId, setCafeUnregisteredStudentDepartmentId] = useState<string | null>(null);
  const [cafeGuestUserDepartmentId, setCafeGuestUserDepartmentId] = useState<string | null>(null);
  const [cafeRegistrationApiKey, setCafeRegistrationApiKey] = useState<string>('');
  const [cafeMenuApiKey, setCafeMenuApiKey] = useState<string>('');
  const [cafeMuhasebeBranchId, setCafeMuhasebeBranchId] = useState<string | null>(null);
  const [cafeMuhasebeBranchName, setCafeMuhasebeBranchName] = useState<string>('');
  const [cafeUnregisteredStudentDepartment, setCafeUnregisteredStudentDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [cafeGuestUserDepartment, setCafeGuestUserDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [showRegistrationApiKey, setShowRegistrationApiKey] = useState(false);
  const [showMenuApiKey, setShowMenuApiKey] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [availableBranches, setAvailableBranches] = useState<Array<{ id: string; name: string; province?: string; district?: string }>>([]);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthStatus, setHealthStatus] = useState<{ success?: boolean; status?: string; timestamp?: string } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const cafeSettings = await ApiService.getCafeSettings();

        setCafeUnregisteredStudentDepartmentId(cafeSettings.cafeUnregisteredStudentDepartmentId);
        setCafeGuestUserDepartmentId(cafeSettings.cafeGuestUserDepartmentId);
        setCafeRegistrationApiKey(cafeSettings.cafeRegistrationApiKey || '');
        setCafeMenuApiKey(cafeSettings.cafeMenuApiKey || '');
        setCafeMuhasebeBranchId(cafeSettings.cafeMuhasebeBranchId || null);
        setCafeMuhasebeBranchName(cafeSettings.cafeMuhasebeBranchName || '');
        setCafeUnregisteredStudentDepartment(cafeSettings.cafeUnregisteredStudentDepartment);
        setCafeGuestUserDepartment(cafeSettings.cafeGuestUserDepartment);
      } catch (error) {
        console.error('Cafe ayarları yüklenirken hata:', error);
        showToast('Cafe ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const generateApiKey = (setter: (value: string) => void) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'cafe_';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setter(result);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      await ApiService.updateCafeSettings({
        cafeUnregisteredStudentDepartmentId: cafeUnregisteredStudentDepartmentId || null,
        cafeGuestUserDepartmentId: cafeGuestUserDepartmentId || null,
        cafeRegistrationApiKey: cafeRegistrationApiKey || null,
        cafeMenuApiKey: cafeMenuApiKey || null,
        cafeMuhasebeBranchId: cafeMuhasebeBranchId || null,
        cafeMuhasebeBranchName: cafeMuhasebeBranchName || null,
      });

      const cafeSettings = await ApiService.getCafeSettings();
      setCafeUnregisteredStudentDepartment(cafeSettings.cafeUnregisteredStudentDepartment);
      setCafeGuestUserDepartment(cafeSettings.cafeGuestUserDepartment);
      setCafeMenuApiKey(cafeSettings.cafeMenuApiKey || '');
      setCafeMuhasebeBranchId(cafeSettings.cafeMuhasebeBranchId || null);
      setCafeMuhasebeBranchName(cafeSettings.cafeMuhasebeBranchName || '');

      showToast('Cafe ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Cafe ayarları güncellenirken hata:', error);
      showToast(error?.message || 'Cafe ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFetchBranches = async () => {
    setBranchLoading(true);
    try {
      const response = await ApiService.getCafeMuhasebeBranches();

      const branchList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.branches)
          ? response.data.branches
          : Array.isArray(response?.branches)
            ? response.branches
            : [];
      setAvailableBranches(branchList);

      if (!branchList.length) {
        showToast('Uygun şube bulunamadı', 'warning');
      }
    } catch (error: any) {
      console.error('Şubeler alınırken hata:', error);
      showToast(error?.message || 'Şube listesi alınamadı', 'error');
    } finally {
      setBranchLoading(false);
    }
  };

  const handleCheckHealth = async () => {
    setHealthChecking(true);
    try {
      const response = await ApiService.getCafeMuhasebeHealth();
      setHealthStatus(response);
      showToast('Bağlantı başarılı', 'success');
    } catch (error: any) {
      console.error('Katalog bağlantı testi hatası:', error);
      setHealthStatus(null);
      showToast(error?.message || 'Bağlantı kontrol edilemedi', 'error');
    } finally {
      setHealthChecking(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'amber';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-amber-600"><path d="M17 8h1a4 4 0 1 1 0 8h-1" /><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" /><line x1="6" x2="6" y1="2" y2="4" /><line x1="10" x2="10" y1="2" y2="4" /><line x1="14" x2="14" y1="2" y2="4" /></svg>
              Cafe Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Cafe kayıt sistemi için departman ve API key ayarlarını yapılandırın.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Kayıt departmanları</span>
            </div>
            <p className='text-sm text-gray-600 mb-6'>
              Cafe üzerinden kayıt olan kullanıcılar yaşlarına göre otomatik olarak bu departmanlara atanır.
            </p>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                  Öğrenciler (18 Yaş ve Altı)
                </label>
                <p className='text-xs text-gray-500 mb-2'>
                  18 yaş ve altındaki kullanıcılar bu departmana kaydedilir.
                </p>
                <DepartmentDropdown
                  value={cafeUnregisteredStudentDepartmentId}
                  onChange={(id) => {
                    setCafeUnregisteredStudentDepartmentId(id);
                    if (!id) setCafeUnregisteredStudentDepartment(null);
                  }}
                  onDepartmentSelect={setCafeUnregisteredStudentDepartment}
                  selectedDepartment={cafeUnregisteredStudentDepartment}
                  disabled={!hasUpdatePermission}
                  placeholder="Departman seçin..."
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  Mezun (18 Yaş Üstü)
                </label>
                <p className='text-xs text-gray-500 mb-2'>
                  18 yaş üstü kullanıcılar bu departmana kaydedilir.
                </p>
                <DepartmentDropdown
                  value={cafeGuestUserDepartmentId}
                  onChange={(id) => {
                    setCafeGuestUserDepartmentId(id);
                    if (!id) setCafeGuestUserDepartment(null);
                  }}
                  onDepartmentSelect={setCafeGuestUserDepartment}
                  selectedDepartment={cafeGuestUserDepartment}
                  disabled={!hasUpdatePermission}
                  placeholder="Departman seçin..."
                />
              </div>
            </div>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>API güvenliği</span>
            </div>
            <p className='text-sm text-gray-600 mb-6'>
              Bu API key, cafe kayıt sistemi endpoint'ine erişim için gereklidir. Güvenli bir yerde saklayın.
            </p>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Cafe Kayıt API Key
              </label>
              <div className='flex flex-wrap gap-2'>
                <div className='relative flex-1 min-w-[12rem]'>
                  <input
                    type={showRegistrationApiKey ? 'text' : 'password'}
                    value={cafeRegistrationApiKey}
                    onChange={(e) => setCafeRegistrationApiKey(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={`${settingsInputClass(accent)} pr-10 font-mono text-sm`}
                    placeholder='API key giriniz veya oluşturun...'
                  />
                  <button
                    type='button'
                    onClick={() => setShowRegistrationApiKey(!showRegistrationApiKey)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showRegistrationApiKey ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                <button
                  type='button'
                  onClick={() => generateApiKey(setCafeRegistrationApiKey)}
                  disabled={!hasUpdatePermission}
                  className='px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Oluştur
                </button>
                {cafeRegistrationApiKey && (
                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(cafeRegistrationApiKey);
                      showToast('API key kopyalandı', 'success');
                    }}
                    className='px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors'
                  >
                    Kopyala
                  </button>
                )}
              </div>
              <p className='text-xs text-gray-500 mt-2'>
                Endpoint: <code className='bg-slate-100 px-1.5 py-0.5 rounded text-amber-800'>POST /external/register</code>
              </p>
            </div>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
              <div>
                <span className={a.badge}>Menü bağlantısı</span>
                <p className='text-sm text-gray-600 mt-2'>
                  YES.Tools kullanıcıları için ürün menüsü ve fiyatlar bu bağlantı üzerinden gösterilir.
                </p>
              </div>
              <button
                type='button'
                onClick={handleCheckHealth}
                disabled={healthChecking}
                className='inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50'
              >
                {healthChecking ? <Loader2 className='w-4 h-4 animate-spin' /> : <RefreshCcw className='w-4 h-4' />}
                Bağlantıyı Test Et
              </button>
            </div>

            <div className='bg-slate-50 rounded-xl border border-slate-200 p-4 sm:p-5 mb-6'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Cafe Menü API Key
              </label>
              <div className='flex flex-wrap gap-2'>
                <div className='relative flex-1 min-w-[12rem]'>
                  <input
                    type={showMenuApiKey ? 'text' : 'password'}
                    value={cafeMenuApiKey}
                    onChange={(e) => setCafeMenuApiKey(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={`${settingsInputClass(accent)} pr-10 font-mono text-sm`}
                    placeholder='Menü API key giriniz veya oluşturun...'
                  />
                  <button
                    type='button'
                    onClick={() => setShowMenuApiKey(!showMenuApiKey)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showMenuApiKey ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                {cafeMenuApiKey && (
                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(cafeMenuApiKey);
                      showToast('API key kopyalandı', 'success');
                    }}
                    className='px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors'
                  >
                    Kopyala
                  </button>
                )}
              </div>
              <p className='text-xs text-gray-500 mt-2'>
                Ürün menüsü ve fiyat listesi isteklerinde kullanılır.
              </p>
            </div>

            {healthStatus?.timestamp && (
              <div className='mb-6 flex items-center gap-2 text-sm text-emerald-700'>
                <CheckCircle2 className='w-4 h-4' />
                Son başarılı kontrol: {new Date(healthStatus.timestamp).toLocaleString('tr-TR')}
              </div>
            )}

            <div className='flex flex-wrap items-center gap-3 mb-4'>
              <button
                type='button'
                onClick={handleFetchBranches}
                disabled={branchLoading}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50 ${a.primaryBtn}`}
              >
                {branchLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : <RefreshCcw className='w-4 h-4' />}
                Şubeleri Getir
              </button>
              {cafeMuhasebeBranchName && (
                <span className='text-sm text-gray-600'>
                  Seçili şube: <strong>{cafeMuhasebeBranchName}</strong>
                </span>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Şube</label>
              <select
                value={cafeMuhasebeBranchId || ''}
                onChange={(e) => {
                  const selected = availableBranches.find((branch) => branch.id === e.target.value);
                  setCafeMuhasebeBranchId(selected?.id || null);
                  setCafeMuhasebeBranchName(selected?.name || '');
                }}
                className={settingsInputClass(accent)}
              >
                <option value=''>Şube seçin...</option>
                {cafeMuhasebeBranchId && cafeMuhasebeBranchName && !availableBranches.some((branch) => branch.id === cafeMuhasebeBranchId) && (
                  <option value={cafeMuhasebeBranchId}>{cafeMuhasebeBranchName}</option>
                )}
                {availableBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}{branch.district || branch.province ? ` - ${[branch.district, branch.province].filter(Boolean).join(' / ')}` : ''}
                  </option>
                ))}
              </select>
              <p className='text-xs text-gray-500 mt-2'>
                Bu seçim yalnızca YES.Tools tarafındaki <strong>Cafe Menüsü</strong> modülünü etkiler.
              </p>
            </div>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Save className='w-4 h-4' />
                )}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function RegistrationSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(true);
  const [registrationPromptText, setRegistrationPromptText] = useState('');
  const [isPasswordResetEnabled, setIsPasswordResetEnabled] = useState(true);
  const [passwordResetPromptText, setPasswordResetPromptText] = useState('');
  const [pendingApprovalDepartmentId, setPendingApprovalDepartmentId] = useState<string | null>(null);
  const [pendingApprovalDepartment, setPendingApprovalDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [cafeUnregisteredStudentDepartmentId, setCafeUnregisteredStudentDepartmentId] = useState<string | null>(null);
  const [cafeGuestUserDepartmentId, setCafeGuestUserDepartmentId] = useState<string | null>(null);
  const [registrationDepartmentIds, setRegistrationDepartmentIds] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [data, cafeSettings] = await Promise.all([
          ApiService.getRegistrationSettings(),
          ApiService.getCafeSettings(),
        ]);
        setIsRegistrationEnabled(data.isRegistrationEnabled !== false);
        setRegistrationPromptText(data.registrationPromptText || '');
        setIsPasswordResetEnabled(data.isPasswordResetEnabled !== false);
        setPasswordResetPromptText(data.passwordResetPromptText || '');
        setPendingApprovalDepartmentId(data.pendingApprovalDepartmentId || null);
        setPendingApprovalDepartment(data.pendingApprovalDepartment || null);
        setRegistrationDepartmentIds(data.registrationDepartmentIds || []);
        setCafeUnregisteredStudentDepartmentId(cafeSettings?.cafeUnregisteredStudentDepartmentId || null);
        setCafeGuestUserDepartmentId(cafeSettings?.cafeGuestUserDepartmentId || null);
      } catch (error) {
        console.error('Kayıt ayarları yüklenirken hata:', error);
        showToast('Kayıt ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const handleSave = async () => {
    if (!hasUpdatePermission) return;
    const hasCafeFallbackDepartments = Boolean(
      cafeUnregisteredStudentDepartmentId && cafeGuestUserDepartmentId
    );

    // Onay bekleyenler departmanı yalnızca kayıt açıkken anlamlı; kapalıyken bu şart
    // sayfadaki diğer ayarların (ör. şifre sıfırlama) kaydedilmesini de engelliyordu.
    if (isRegistrationEnabled && !pendingApprovalDepartmentId && !hasCafeFallbackDepartments) {
      showToast("Onay bekleyenler departmanı seçilmelidir veya Cafe ayarlarında 'Öğrenciler' ve 'Mezun' departmanları birlikte tanımlı olmalıdır.", 'warning');
      return;
    }

    setSaving(true);
    try {
      const response = await ApiService.updateRegistrationSettings({
        isRegistrationEnabled,
        registrationPromptText: registrationPromptText.trim(),
        isPasswordResetEnabled,
        passwordResetPromptText: passwordResetPromptText.trim(),
        pendingApprovalDepartmentId,
      });
      setPendingApprovalDepartmentId(response.pendingApprovalDepartmentId || null);
      setPendingApprovalDepartment(response.pendingApprovalDepartment || null);
      // Kayıtta görünecek departmanları da kaydet
      await ApiService.updateRegistrationDepartments(registrationDepartmentIds);
      showToast('Kayıt ve şifre ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Kayıt ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <UserCheck className='w-6 h-6 text-sky-600' />
              Kayıt ve Şifre Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Giriş ekranındaki &quot;Kayıt Ol&quot; ve &quot;Şifremi Unuttum&quot; bölümlerini açıp kapatabilir, metinlerini değiştirebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <div className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Kayıt anahtarı</span>
            </div>
            <div className='flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200'>
              <div className='flex items-center gap-3'>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRegistrationEnabled ? 'bg-sky-100 text-sky-700' : 'bg-red-100 text-red-500'
                  }`}>
                  <UserCheck className='w-5 h-5' />
                </div>
                <div>
                  <p className='font-semibold text-gray-800'>Kayıt Sistemi</p>
                  <p className='text-sm text-gray-500'>
                    {isRegistrationEnabled
                      ? 'Yeni kullanıcılar /register sayfasından kayıt olabilir.'
                      : 'Kayıt kapalı. Yeni kullanıcılar kayıt olamaz.'}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => hasUpdatePermission && setIsRegistrationEnabled(!isRegistrationEnabled)}
                disabled={!hasUpdatePermission}
                title={hasUpdatePermission ? '' : 'Bu ayarı değiştirmek için yetkiniz yok'}
                className={`relative flex w-14 h-7 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 ${!hasUpdatePermission ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${isRegistrationEnabled ? 'bg-sky-600' : 'bg-gray-300'
                  }`}
                aria-pressed={isRegistrationEnabled}
              >
                <span
                  className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${isRegistrationEnabled ? 'translate-x-7' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>

          <div className={`p-4 rounded-xl border text-sm shadow-sm ${isRegistrationEnabled
            ? 'bg-sky-50 border-sky-200 text-sky-900'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}>
            {isRegistrationEnabled ? (
              <>
                <p className='font-semibold mb-1'>Kayıt açık</p>
                <ul className='list-disc list-inside space-y-1 text-sky-800/90'>
                  <li>Onay bekleyenler departmanı seçilirse yeni kayıtlar doğrudan bu departmana atanır</li>
                  <li>Onay bekleyenler boş bırakılırsa ve Cafe ayarlarında iki departman da seçiliyse yaşa göre otomatik atama yapılır</li>
                  <li>Cafe fallback için gerekli alanlar: <strong>Öğrenciler</strong> ve <strong>Mezun</strong></li>
                </ul>
              </>
            ) : (
              <>
                <p className='font-semibold mb-1'>Kayıt kapalı</p>
                <p className='text-red-700'>Giriş sayfasında &quot;Kaydol&quot; butonu gizlenecek. Mevcut kullanıcılar etkilenmez.</p>
              </>
            )}
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Onay bekleyenler</span>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Onay Bekleyenler Departmanı
            </label>
            <DepartmentDropdown
              value={pendingApprovalDepartmentId}
              onChange={(departmentId) => {
                setPendingApprovalDepartmentId(departmentId);
                if (!departmentId) setPendingApprovalDepartment(null);
              }}
              onDepartmentSelect={setPendingApprovalDepartment}
              selectedDepartment={pendingApprovalDepartment}
              disabled={!hasUpdatePermission}
              placeholder='Departman Seçiniz...'
            />
            <p className='text-xs text-gray-500 mt-2'>
              Yeni kayıt olan kullanıcılar doğrudan bu departmana atanır ve onay/review süreçlerinde işlenir.
            </p>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Kayıtta görünecek departmanlar</span>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Kayıt Ekranında Seçilebilecek Departmanlar
            </label>
            <div className='rounded-xl border border-slate-200 overflow-hidden max-h-72 overflow-y-auto'>
              <DepartmentTreeSelect
                value={registrationDepartmentIds}
                onChange={setRegistrationDepartmentIds}
                disabled={!hasUpdatePermission}
                multiple={true}
              />
            </div>
            <p className='text-xs text-gray-500 mt-2'>
              Seçtiğiniz departmanlar kayıt ekranında liste olarak görünür; kullanıcı birini seçip o departmana kayıt olur. Boş bırakılırsa eski sınıf-seçimli kayıt akışı geçerli olur.
            </p>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Giriş metni</span>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Kayıt Olma Yönlendirme Metni
            </label>
            <input
              type='text'
              value={registrationPromptText}
              onChange={(e) => setRegistrationPromptText(e.target.value)}
              disabled={!hasUpdatePermission}
              placeholder='Ör: Hesabın yok mu?'
              className={settingsInputClass(accent)}
            />
            <p className='text-xs text-gray-500 mt-2'>
              Giriş sayfasında kayıt olma linki öncesinde görünecek metin (Boş bırakılırsa &quot;Hesabın yok mu?&quot; gösterilir).
            </p>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Şifremi unuttum</span>
            </div>
            <div className='flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200'>
              <div className='flex items-center gap-3'>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPasswordResetEnabled ? 'bg-sky-100 text-sky-700' : 'bg-red-100 text-red-500'
                  }`}>
                  <KeyRound className='w-5 h-5' />
                </div>
                <div>
                  <p className='font-semibold text-gray-800'>Şifre Sıfırlama</p>
                  <p className='text-sm text-gray-500'>
                    {isPasswordResetEnabled
                      ? 'Giriş ekranında "Şifremi unuttum" görünür; kullanıcının e-postasına 6 haneli doğrulama kodu gider.'
                      : 'Kapalı. Giriş ekranında "Şifremi unuttum" görünmez ve sıfırlama istekleri reddedilir.'}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => hasUpdatePermission && setIsPasswordResetEnabled(!isPasswordResetEnabled)}
                disabled={!hasUpdatePermission}
                title={hasUpdatePermission ? '' : 'Bu ayarı değiştirmek için yetkiniz yok'}
                className={`relative flex w-14 h-7 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 ${!hasUpdatePermission ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${isPasswordResetEnabled ? 'bg-sky-600' : 'bg-gray-300'
                  }`}
                aria-pressed={isPasswordResetEnabled}
              >
                <span
                  className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${isPasswordResetEnabled ? 'translate-x-7' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>

            {isPasswordResetEnabled && (
              <>
                <div className='mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900 shadow-sm'>
                  <p className='font-semibold mb-1'>E-posta (SMTP) ayarı şart</p>
                  <p className='text-amber-800/90'>
                    Doğrulama kodu e-posta ile gönderilir. <strong>E-posta (SMTP)</strong> sekmesi doldurulmamışsa
                    kullanıcı hiçbir mesaj almaz — ekranda yine de &quot;kod gönderildi&quot; yazar (adres sızdırmamak için).
                    Açmadan önce SMTP sekmesindeki bağlantı sınamasını çalıştırın.
                  </p>
                </div>

                <label className='block text-sm font-medium text-gray-700 mt-4 mb-2'>
                  Şifremi Unuttum Link Metni
                </label>
                <input
                  type='text'
                  value={passwordResetPromptText}
                  onChange={(e) => setPasswordResetPromptText(e.target.value)}
                  disabled={!hasUpdatePermission}
                  placeholder='Ör: Şifremi unuttum'
                  className={settingsInputClass(accent)}
                />
                <p className='text-xs text-gray-500 mt-2'>
                  Giriş sayfasındaki bağlantının yazısı (Boş bırakılırsa &quot;Şifremi unuttum&quot; gösterilir).
                  Kod 15 dakika geçerlidir, 5 yanlış denemede iptal olur ve aynı hesap için günde en fazla 5 kez istenebilir.
                  Bağlantı yerine kod kullanıldığı için kurulumun alan adını bilmek gerekmez; kullanıcı kodu bulunduğu sayfaya girer.
                </p>
              </>
            )}
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='button'
                onClick={handleSave}
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

function N8nSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [n8nLatexExtractorWebhookUrl, setN8nLatexExtractorWebhookUrl] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const n8nSettings = await ApiService.getN8nSettings();

        setN8nLatexExtractorWebhookUrl(n8nSettings.n8nLatexExtractorWebhookUrl || '');
      } catch (error) {
        console.error('n8n ayarları yüklenirken hata:', error);
        showToast('n8n ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      await ApiService.updateN8nSettings({
        n8nLatexExtractorWebhookUrl: n8nLatexExtractorWebhookUrl || null,
      });

      showToast('n8n ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('n8n ayarları güncellenirken hata:', error);
      showToast(error?.message || 'n8n ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'indigo';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <ExternalLink className='w-6 h-6 text-indigo-600' />
              Yapay Zeka Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Dosyalardan LaTeX ayıklama webhook bağlantısını buradan yönetebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Webhook adresleri</span>
            </div>
            <div className='grid grid-cols-1 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <ExternalLink className='w-4 h-4 text-indigo-600' />
                  LaTeX Extractor Webhook URL
                </label>
                <input
                  type='text'
                  value={n8nLatexExtractorWebhookUrl}
                  onChange={(e) => setN8nLatexExtractorWebhookUrl(e.target.value)}
                  disabled={!hasUpdatePermission}
                  className={settingsInputClass(accent)}
                  placeholder='https://...'
                />
              </div>
            </div>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Save className='w-4 h-4' />
                )}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

const normalizeAiTeacherModels = (models?: AiTeacherAudioModel[]): AiTeacherAudioModel[] => {
  const source = models?.length ? models : AI_TEACHER_AUDIO_MODELS;

  return source.map((model) => {
    const fallback = AI_TEACHER_AUDIO_MODELS.find((item) => item.id === model.id);
    const mode: AiTeacherAudioModel['mode'] =
      model.mode === 'live' ? 'live' : model.mode === 'browser' ? 'browser' : model.mode === 'none' ? 'none' : 'tts';

    return {
      ...fallback,
      ...model,
      mode,
      description: model.description || fallback?.description,
    };
  });
};

function AITeacherSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audioModel, setAudioModel] = useState(AI_TEACHER_AUDIO_MODELS[0].id);
  const [supportedModels, setSupportedModels] = useState<AiTeacherAudioModel[]>(AI_TEACHER_AUDIO_MODELS);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const settings = await ApiService.getAITeacherSettings();
        const models = normalizeAiTeacherModels(settings.supportedAudioModels);

        setSupportedModels(models);
        setAudioModel(settings.aiTeacherAudioModel || models[0]?.id || AI_TEACHER_AUDIO_MODELS[0].id);
      } catch (error) {
        console.error('AI Öğretmenim ayarları yüklenirken hata:', error);
        showToast('AI Öğretmenim ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      const response = await ApiService.updateAITeacherSettings({
        aiTeacherAudioModel: audioModel,
      });

      if (response?.aiTeacherAudioModel) setAudioModel(response.aiTeacherAudioModel);
      showToast('AI Öğretmenim ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('AI Öğretmenim ayarları güncellenirken hata:', error);
      showToast(error?.message || 'AI Öğretmenim ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const selectedModel = supportedModels.find((model) => model.id === audioModel);
  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className="mb-5">
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Bot className="w-6 h-6 text-cyan-600" />
              AI Öğretmenim
            </h1>
            <p className="text-sm text-gray-600 mt-1">Eksikliklerim alanındaki AI öğretmenin ses modelini buradan seçebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className={SETTINGS_SECTION}>
              <div className="mb-3">
                <span className={a.badge}>Ses modeli</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {supportedModels.map((model) => {
                  const isSelected = audioModel === model.id;
                  const Icon = model.mode === 'live' ? Radio : Volume2;

                  return (
                    <button
                      key={model.id}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={!hasUpdatePermission || saving}
                      onClick={() => setAudioModel(model.id)}
                      className={`text-left p-4 rounded-xl border transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                        isSelected
                          ? "border-cyan-300 bg-cyan-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-cyan-200 hover:bg-cyan-50/40"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isSelected ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900">{model.label}</p>
                              <p className="text-xs text-gray-500 mt-1 break-all">{model.id}</p>
                            </div>
                            {isSelected && <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0" />}
                          </div>
                          {model.description ? <p className="text-sm text-gray-600 mt-3">{model.description}</p> : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedModel?.mode === "live" && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Live API modeli her ses üretiminde anlık bağlantı açar; gecikme ve ses karakteri TTS modelinden farklı olabilir.
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0">
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!hasUpdatePermission || saving}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Koç mektupları: imza adı, kurumun kendine koyduğu referans çizgi (SAY/EA/SÖZ sıralama), otomatik onay.
function CoachSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coachName, setCoachName] = useState('Koçun');
  const [referans, setReferans] = useState<{ SAY: string; EA: string; SÖZ: string }>({ SAY: '200000', EA: '100000', SÖZ: '100000' });
  const [autoApprove, setAutoApprove] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await ApiService.getCoachSettings();
        setCoachName(r.coachName || 'Koçun');
        setReferans({ SAY: String(r.coachReferans?.SAY ?? ''), EA: String(r.coachReferans?.EA ?? ''), SÖZ: String(r.coachReferans?.['SÖZ'] ?? '') });
        setAutoApprove(r.coachLetterAutoApprove !== false);
      } catch (error: any) {
        showToast(error?.message || 'Koç ayarları yüklenemedi', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;
    setSaving(true);
    try {
      const coachReferans: Record<string, number> = {};
      (['SAY', 'EA', 'SÖZ'] as const).forEach((k) => { const n = Number(referans[k]); if (n > 0) coachReferans[k] = Math.round(n); });
      await ApiService.updateCoachSettings({ coachName: coachName.trim() || null, coachReferans, coachLetterAutoApprove: autoApprove });
      showToast('Koç ayarları güncellendi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Koç ayarları güncellenirken hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];
  const input = settingsInputClass(accent);

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className="mb-5">
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <MessageSquare className="w-6 h-6 text-cyan-600" />
              Koç Mektupları
            </h1>
            <p className="text-sm text-gray-600 mt-1">Koç öğrenciye haftada üç kez yazar: Pazar söz, Çarşamba ara not, Cumartesi hesap. Buradaki ad imzada görünür; referans çizgi kurumun kendine koyduğu hedeftir (öğrenciye "garanti" olarak gösterilmez).</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>Kimlik</span></div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Koçun imza adı</label>
              <input type="text" value={coachName} onChange={(e) => setCoachName(e.target.value)} disabled={!hasUpdatePermission || saving} maxLength={60} className={input} placeholder="Koçun" />
              <p className="text-xs text-gray-500 mt-1">Mektubun son satırı: "— {coachName || 'Koçun'}". Koç insan gibi yazar; sorulursa yapay zekâ olduğunu inkâr etmez.</p>
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>Referans çizgi (sıralama)</span></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(['SAY', 'EA', 'SÖZ'] as const).map((k) => (
                  <div key={k}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{k}</label>
                    <input type="number" min={1000} max={3000000} step={1000} value={referans[k]} onChange={(e) => setReferans({ ...referans, [k]: e.target.value })} disabled={!hasUpdatePermission || saving} className={input} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Yörünge bu çizgiye göre ölçülür: "bu sözle çizgiye kaçıncı haftada varır / kapanmıyor, eksik X puan". Boş bırakılan tür varsayılanı kullanır (SAY 200.000, EA 100.000, SÖZ 100.000).</p>
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>Onay</span></div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} disabled={!hasUpdatePermission || saving} className="mt-1 h-4 w-4" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Mektuplar onaysız gitsin</span>
                  <span className="block text-xs text-gray-500">Kapalıysa üretilen her mektup Koç Gözlem'de "onay bekliyor" olarak durur; gözlemci okur, gerekirse düzeltir, onaylar. Düzeltmeler sonraki üretimlere örnek olur.</span>
                </span>
              </label>
            </div>

            <div className="sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0">
              <div className="flex justify-end">
                <button type="submit" disabled={!hasUpdatePermission || saving} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Koçun telefon araması (24 Eki): YES Tools'un 0282 hattından yapay zekâ asistanı, planında 3 çalışma günüdür iş yapmayan
// öğrenciyi akşam saatlerinde arar (kısa koç görüşmesi). Ayarlar kurum başına buradan; boş alan sunucu ayarına düşer.
function CoachCallSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<CoachCallSettings | null>(null);
  const [form, setForm] = useState({
    enabled: false, voice: 'vesper', maxPerRun: 5, livekitUrl: '', livekitApiKey: '', livekitApiSecret: '', agentName: '', publicUrl: '', appUrl: '', openaiApiKey: '', kimlik: '',
    ilkZiyaret: false, ilkZiyaretMinDk: 60, bag: false, bagAralikGun: 14, yorumUrl: '', yorumKimden: 'MEMNUN' as 'MEMNUN' | 'HERKES', akademiKimlik: '', gelenAnahtar: '',
    waEnabled: false, waSendUrl: '', waWorkspaceId: '', waToken: '', waNumara: '', waSablonHosGeldin: '', waSablonOdev: '', waSablonBekleyen: '',
  });
  const [clear, setClear] = useState({ openai: false, secret: false, gelen: false, wa: false });
  const [testPhone, setTestPhone] = useState('');
  const [testTur, setTestTur] = useState<'SESSIZLIK' | 'ILK_ZIYARET' | 'BAG'>('SESSIZLIK');
  const [testing, setTesting] = useState(false);
  const [calls, setCalls] = useState<ObserveCallRow[] | null>(null);
  const [callFilter, setCallFilter] = useState('');
  const [openCall, setOpenCall] = useState<string | null>(null);

  const apply = (r: CoachCallSettings) => {
    setData(r);
    setForm({
      enabled: r.enabled, voice: r.voice, maxPerRun: r.maxPerRun, livekitUrl: r.livekitUrl, livekitApiKey: r.livekitApiKey, livekitApiSecret: '', agentName: r.agentName, publicUrl: r.publicUrl, appUrl: r.appUrl, openaiApiKey: '', kimlik: r.kimlik || '',
      ilkZiyaret: !!r.ilkZiyaret, ilkZiyaretMinDk: r.ilkZiyaretMinDk || 60, bag: !!r.bag, bagAralikGun: r.bagAralikGun || 14, yorumUrl: r.yorumUrl || '', yorumKimden: r.yorumKimden === 'HERKES' ? 'HERKES' : 'MEMNUN', akademiKimlik: r.akademiKimlik || '', gelenAnahtar: '',
      waEnabled: !!r.waEnabled, waSendUrl: r.waSendUrl || '', waWorkspaceId: r.waWorkspaceId || '', waToken: '', waNumara: r.waNumara || '', waSablonHosGeldin: r.waSablonHosGeldin || '', waSablonOdev: r.waSablonOdev || '', waSablonBekleyen: r.waSablonBekleyen || '',
    });
    setClear({ openai: false, secret: false, gelen: false, wa: false });
  };
  const loadCalls = useCallback(async (reason = '') => {
    try { setCalls(await ApiService.getCoachCalls({ reason: reason || undefined, limit: 50 })); }
    catch { setCalls([]); }
  }, []);
  useEffect(() => { loadCalls(callFilter); }, [loadCalls, callFilter]);
  useEffect(() => {
    (async () => {
      try { apply(await ApiService.getCoachCallSettings()); }
      catch (error: any) { showToast(error?.message || 'Telefon asistanı ayarları yüklenemedi', 'error'); }
      finally { setLoading(false); }
    })();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;
    setSaving(true);
    try {
      // Bağlantı alanları ve kimlik yalnız üst admin gönderir (sunucu diğerlerinde reddeder)
      const baglanti = data?.baglantiDuzenlenebilir
        ? {
          livekitUrl: form.livekitUrl, livekitApiKey: form.livekitApiKey, livekitApiSecret: form.livekitApiSecret, livekitApiSecretClear: clear.secret, agentName: form.agentName, publicUrl: form.publicUrl, appUrl: form.appUrl, kimlik: form.kimlik,
          akademiKimlik: form.akademiKimlik, gelenAnahtar: form.gelenAnahtar, gelenAnahtarClear: clear.gelen,
          waSendUrl: form.waSendUrl, waWorkspaceId: form.waWorkspaceId, waToken: form.waToken, waTokenClear: clear.wa,
        }
        : {};
      apply(await ApiService.updateCoachCallSettings({
        enabled: form.enabled, voice: form.voice, maxPerRun: Number(form.maxPerRun) || 5,
        openaiApiKey: form.openaiApiKey, openaiApiKeyClear: clear.openai,
        ilkZiyaret: form.ilkZiyaret, ilkZiyaretMinDk: Number(form.ilkZiyaretMinDk) || 60, bag: form.bag, bagAralikGun: Number(form.bagAralikGun) || 14, yorumUrl: form.yorumUrl.trim(), yorumKimden: form.yorumKimden,
        waEnabled: form.waEnabled, waNumara: form.waNumara.trim(), waSablonHosGeldin: form.waSablonHosGeldin.trim(), waSablonOdev: form.waSablonOdev.trim(), waSablonBekleyen: form.waSablonBekleyen.trim(),
        ...baglanti,
      }));
      showToast('Telefon asistanı ayarları kaydedildi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Ayarlar kaydedilemedi', 'error');
    } finally {
      setSaving(false);
    }
  };

  const testAra = async () => {
    if (!testPhone.trim()) return;
    setTesting(true);
    try {
      await ApiService.testCoachCall(testPhone.trim(), testTur);
      showToast('Test araması başladı; telefon birazdan 0282 hattından çalacak.', 'success');
      window.setTimeout(() => loadCalls(callFilter), 20000);
    } catch (error: any) {
      showToast(error?.message || 'Test araması başlatılamadı', 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <LoadingState />;
  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];
  const input = settingsInputClass(accent);
  const dis = !hasUpdatePermission || saving;
  // LiveKit, asistan adı, API ve panel adresi, kimlik: 0282 hattından kurum adına konuşulduğu için yalnız üst admin
  const disRoot = dis || !data?.baglantiDuzenlenebilir;
  const etkin = data?.etkin;

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className="mb-5">
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Phone className="w-6 h-6 text-cyan-600" />
              Telefon Asistanı
            </h1>
            <p className="text-sm text-gray-600 mt-1">Koçun yapay zekâ asistanı, planında 3 çalışma günüdür iş yapmayan öğrenciyi kendi numarasından arar: "YES Tools platformundan, koçunun yapay zekâ asistanıyım". 3-4 dakikada engeli dinler, bugün yapabileceği tek işi önerir, söz alır; sonuç Koç Gözlem'e düşer, ciddi durumda öğretmene haber gider. Ara verilen, "beni aramayın" diyen ya da numarası yanlış çıkan öğrenci aranmaz; hatta aynı anda en çok 2 arama yapılır.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>Otomatik arama</span></div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} disabled={dis} className="mt-1 h-4 w-4" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Sessiz öğrencileri kendiliğinden ara</span>
                  <span className="block text-xs text-gray-500">Hafta içi 17:30–20:30, hafta sonu 11:00–19:00. Aynı öğrenci en çok 3 günde bir; açmazsa ertesi gün bir kez daha, sonra durur. Kapalıyken yalnız Koç Gözlem'den "Asistan şimdi arasın" ile aranır.</span>
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ses</label>
                  <select value={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.value })} disabled={dis} className={input}>
                    {(data?.voices || ['vesper']).map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yarım saatte en çok arama</label>
                  <input type="number" min={1} max={30} value={form.maxPerRun} onChange={(e) => setForm({ ...form, maxPerRun: Number(e.target.value) })} disabled={dis} className={input} />
                </div>
              </div>
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>Akademi aramaları</span></div>
              <p className="text-xs text-gray-500 mb-3">Kapı (QR) kaydına göre, aynı akşam penceresinde. İçerideyken aranmaz. Asistan doğal konuşur, satış yapmaz: paket konusunu kendisi açmaz; sorulursa kısaca anlatır, fiyat için Ödemeler sayfasını SMS'ler, lise öğrencisine veliyle görüşmeyi önerir.</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.ilkZiyaret} onChange={(e) => setForm({ ...form, ilkZiyaret: e.target.checked })} disabled={dis} className="mt-1 h-4 w-4" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">İlk ziyaret araması</span>
                  <span className="block text-xs text-gray-500">Hoş geldin girişiyle bugün akademiye ilk kez gelen, içeride en az belirtilen süre kalıp çıkan yeni kayıtlıyı arar: "İlk günün nasıl geçti?" Kişi başına bir kez.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer mt-3">
                <input type="checkbox" checked={form.bag} onChange={(e) => setForm({ ...form, bag: e.target.checked })} disabled={dis} className="mt-1 h-4 w-4" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Paketli öğrenciyle bağ</span>
                  <span className="block text-xs text-gray-500">Ücretli paketi olan ve bugün akademiye gelen öğrenciyi ara ara arar: hazırlık nasıl, akademide bir ihtiyacı var mı. Önceki görüşmenin notunu hatırlar. Koşu başına en çok 2.</span>
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İlk ziyarette içeride en az (dk)</label>
                  <input type="number" min={15} max={480} value={form.ilkZiyaretMinDk} onChange={(e) => setForm({ ...form, ilkZiyaretMinDk: Number(e.target.value) })} disabled={dis} className={input} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aynı öğrenciyle bağ araması en az (gün arayla)</label>
                  <input type="number" min={7} max={90} value={form.bagAralikGun} onChange={(e) => setForm({ ...form, bagAralikGun: Number(e.target.value) })} disabled={dis} className={input} />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Google yorum bağlantısı</label>
                <input type="url" value={form.yorumUrl} onChange={(e) => setForm({ ...form, yorumUrl: e.target.value })} disabled={dis} className={input} placeholder="https://g.page/r/.../review" />
                <p className="text-xs text-gray-500 mt-1">Google İşletme Profili → "Yorum iste" bağlantısı. Yalnız ilk ziyaret aramasında, kişi kabul ederse SMS'le gider; asistan yıldız ya da puan istemez.</p>
                <div className="mt-2 flex flex-col gap-1 text-sm">
                  <label className="flex items-start gap-2"><input type="radio" name="yorumKimden" checked={form.yorumKimden === 'MEMNUN'} onChange={() => setForm({ ...form, yorumKimden: 'MEMNUN' })} disabled={dis} className="mt-1" /> <span>Yalnız memnun kaldığını söyleyene sorsun</span></label>
                  <label className="flex items-start gap-2"><input type="radio" name="yorumKimden" checked={form.yorumKimden === 'HERKES'} onChange={() => setForm({ ...form, yorumKimden: 'HERKES' })} disabled={dis} className="mt-1" /> <span>Memnuniyetine bakmadan herkese sorsun</span></label>
                </div>
                {form.yorumKimden === 'MEMNUN' && <p className="text-xs text-amber-700 mt-1">Google, yalnız memnun müşterilerden seçerek yorum istemeyi kurallarına aykırı sayıyor; fark edilirse yorumlar kaldırılabilir. Kurala uygun seçenek "herkese sorsun".</p>}
              </div>
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>WhatsApp</span> <span className={`ml-2 text-xs ${data?.waHazir ? 'text-emerald-700' : 'text-amber-700'}`}>{data?.waHazir ? 'YES.Social bağlantısı tam' : 'bağlantı eksik — mesaj gitmez'}</span></div>
              <p className="text-xs text-gray-500 mb-3">Mesajlar YES.Social'ın WhatsApp hattından, Meta'nın onayladığı şablonlarla gider. Yalnız WhatsApp izni olan öğrenciye: kayıt formundaki kutu ya da Koçum'daki "WhatsApp'tan gelsin mi?" kartı. 3 gündür iş yoksa önce WhatsApp, ertesi gün hâlâ dönmediyse asistan arar. Aynı kişiye 7 günde en çok 2 hatırlatma.</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.waEnabled} onChange={(e) => setForm({ ...form, waEnabled: e.target.checked })} disabled={dis} className="mt-1 h-4 w-4" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Koçum WhatsApp mesajları</span>
                  <span className="block text-xs text-gray-500">Kayıtta hoş geldin, öğretmen ödev verince "yeni ödev", 3 gündür iş yoksa "bekleyen iş".</span>
                </span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Öğrenciye gösterilen numara</label>
                  <input type="text" value={form.waNumara} onChange={(e) => setForm({ ...form, waNumara: e.target.value })} disabled={dis} className={input} placeholder="0282 606 06 35" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {([['waSablonHosGeldin', 'Hoş geldin', 'hos_geldin'], ['waSablonOdev', 'Yeni ödev', 'yeni_odev'], ['waSablonBekleyen', 'Bekleyen iş', 'bekleyen_is']] as const).map(([k, l, ph]) => (
                    <div key={k}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{l} şablonu</label>
                      <input type="text" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} disabled={dis} className={input} placeholder={ph} />
                    </div>
                  ))}
                </div>
              </div>
              {!data?.baglantiDuzenlenebilir && <p className="mt-3 text-xs text-gray-500">YES.Social bağlantısını yalnız üst admin değiştirebilir.</p>}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">YES.Social şablon ucu</label>
                  <input type="text" value={form.waSendUrl} onChange={(e) => setForm({ ...form, waSendUrl: e.target.value })} disabled={disRoot} className={input} placeholder="https://social.yes.tools/api/whatsapp/template" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">YES.Social çalışma alanı kimliği</label>
                  <input type="text" value={form.waWorkspaceId} onChange={(e) => setForm({ ...form, waWorkspaceId: e.target.value })} disabled={disRoot} className={input} placeholder="7ac73b9d-…" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Şablon ucu anahtarı</label>
                  <input type="password" autoComplete="new-password" value={form.waToken} onChange={(e) => setForm({ ...form, waToken: e.target.value })} disabled={disRoot} className={input} placeholder={data?.waTokenSet ? `Kayıtlı (${data.waTokenHint})` : "Social → Hesaplar → WhatsApp kartından"} />
                  {data?.waTokenSet && (
                    <label className="mt-1 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={clear.wa} onChange={(e) => setClear({ ...clear, wa: e.target.checked })} disabled={disRoot} /> Kayıtlıyı sil</label>
                  )}
                </div>
              </div>
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>OpenAI anahtarı</span></div>
              <input type="password" autoComplete="new-password" value={form.openaiApiKey} onChange={(e) => setForm({ ...form, openaiApiKey: e.target.value })} disabled={dis} className={input} placeholder={data?.openaiApiKeySet ? `Kayıtlı (${data.openaiApiKeyHint}) — değiştirmek için yazın` : 'sk-...'} />
              <p className="text-xs text-gray-500 mt-1">Asistan görüşmeyi bu anahtarla yapar (ücret kurumun OpenAI hesabına). Boş bırakılırsa asistanın kendi anahtarı kullanılır.</p>
              {data?.openaiApiKeySet && (
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={clear.openai} onChange={(e) => setClear({ ...clear, openai: e.target.checked })} disabled={dis} /> Kayıtlı anahtarı sil</label>
              )}
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>LiveKit bağlantısı</span> <span className={`ml-2 text-xs ${etkin?.livekitHazir ? 'text-emerald-700' : 'text-amber-700'}`}>{etkin?.livekitHazir ? 'bağlantı bilgileri tam' : 'eksik — arama yapılamaz'}</span></div>
              {!data?.baglantiDuzenlenebilir && <p className="mb-3 text-xs text-gray-500">Bu bölümü yalnız üst admin değiştirebilir.</p>}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asistanın kendini tanıtışı</label>
                  <input type="text" maxLength={60} value={form.kimlik} onChange={(e) => setForm({ ...form, kimlik: e.target.value })} disabled={disRoot} className={input} placeholder="YES Tools platformundan" />
                  <p className="text-xs text-gray-500 mt-1">"Ben <b>{form.kimlik.trim() || 'YES Tools platformundan'}</b>, koçunun yapay zekâ asistanıyım" diye başlar.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sunucu adresi</label>
                  <input type="text" value={form.livekitUrl} onChange={(e) => setForm({ ...form, livekitUrl: e.target.value })} disabled={disRoot} className={input} placeholder={etkin?.livekitUrl ? `kullanılıyor: ${etkin.livekitUrl}` : 'wss://...livekit.cloud'} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API anahtarı</label>
                    <input type="text" value={form.livekitApiKey} onChange={(e) => setForm({ ...form, livekitApiKey: e.target.value })} disabled={disRoot} className={input} placeholder="API..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API gizli anahtarı</label>
                    <input type="password" autoComplete="new-password" value={form.livekitApiSecret} onChange={(e) => setForm({ ...form, livekitApiSecret: e.target.value })} disabled={disRoot} className={input} placeholder={data?.livekitApiSecretSet ? `Kayıtlı (${data.livekitApiSecretHint})` : ''} />
                    {data?.livekitApiSecretSet && (
                      <label className="mt-1 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={clear.secret} onChange={(e) => setClear({ ...clear, secret: e.target.checked })} disabled={disRoot} /> Kayıtlıyı sil</label>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Asistan adı</label>
                    <input type="text" value={form.agentName} onChange={(e) => setForm({ ...form, agentName: e.target.value })} disabled={disRoot} className={input} placeholder={etkin?.agentName || 'agent-social'} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bu sistemin API adresi</label>
                    <input type="text" value={form.publicUrl} onChange={(e) => setForm({ ...form, publicUrl: e.target.value })} disabled={disRoot} className={input} placeholder={etkin?.publicUrl || 'https://api...'} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Panel adresi</label>
                    <input type="text" value={form.appUrl} onChange={(e) => setForm({ ...form, appUrl: e.target.value })} disabled={disRoot} className={input} placeholder={etkin?.appUrl || window.location.origin} />
                  </div>
                </div>
                <p className="text-xs text-gray-500">API adresine asistan görüşme bilgisini almak ve sonucu yazmak için döner. Panel adresini öğrenci "platforma nereden giriyordum" derse söyler. Boş alanlar sunucu ayarını kullanır.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Akademi aramalarında tanıtım</label>
                    <input type="text" maxLength={60} value={form.akademiKimlik} onChange={(e) => setForm({ ...form, akademiKimlik: e.target.value })} disabled={disRoot} className={input} placeholder="YES Akademi'nin" />
                    <p className="text-xs text-gray-500 mt-1">"Ben <b>{form.akademiKimlik.trim() || "YES Akademi'nin"}</b> yapay zekâ asistanıyım"</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gelen arama anahtarı</label>
                    <input type="password" autoComplete="new-password" value={form.gelenAnahtar} onChange={(e) => setForm({ ...form, gelenAnahtar: e.target.value })} disabled={disRoot} className={input} placeholder={data?.gelenAnahtarSet ? `Kayıtlı (${data.gelenAnahtarHint})` : 'en az 24 karakter'} />
                    {data?.gelenAnahtarSet && (
                      <label className="mt-1 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={clear.gelen} onChange={(e) => setClear({ ...clear, gelen: e.target.checked })} disabled={disRoot} /> Kayıtlıyı sil</label>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Aradığımız numara 0282'yi geri ararsa asistan kişiyi tanır ve önceki görüşmeden devam eder. Aynı anahtar asistanda KOCUM_GELEN_ANAHTAR olarak tanımlı olmalı.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className={SETTINGS_SECTION}>
              <div className="mb-3"><span className={a.badge}>Test araması</span></div>
              <div className="flex flex-wrap items-end gap-2">
                <input type="tel" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} disabled={!hasUpdatePermission || testing} className={`${input} max-w-xs`} placeholder="05xx xxx xx xx" />
                <select value={testTur} onChange={(e) => setTestTur(e.target.value as typeof testTur)} disabled={!hasUpdatePermission || testing} className={`${input} max-w-xs`}>
                  <option value="SESSIZLIK">Koç araması (3 gündür iş yok)</option>
                  <option value="ILK_ZIYARET">İlk ziyaret araması</option>
                  <option value="BAG">Paketli öğrenciyle bağ</option>
                </select>
                <button type="button" onClick={testAra} disabled={!hasUpdatePermission || testing || !testPhone.trim()} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-60">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} Test araması yap
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Önce ayarları kaydedin. Asistan bu numarayı seçilen türde örnek bir durumla arar (koç araması: 3 gündür iş yok; ilk ziyaret: bugün 10:15–14:20 akademideydi; bağ: 3 aylık paketli); sonuç aşağıdaki listeye düşer.</p>
            </div>

            <div className="sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0">
              <div className="flex justify-end">
                <button type="submit" disabled={dis} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </form>

          <div className={`${SETTINGS_SECTION} mt-5`}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className={a.badge}>Son görüşmeler</span>
              <select value={callFilter} onChange={(e) => setCallFilter(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-xs">
                <option value="">Hepsi</option>
                {Object.entries(ARAMA_TUR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button type="button" onClick={() => loadCalls(callFilter)} className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"><RefreshCcw className="h-3 w-3" /> Yenile</button>
            </div>
            {calls === null ? <p className="text-xs text-gray-500">Yükleniyor…</p> : calls.length === 0 ? <p className="text-xs text-gray-500">Henüz görüşme yok.</p> : (
              <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {calls.map((c) => (
                  <li key={c.id} className="px-3 py-2 text-xs">
                    <button type="button" onClick={() => setOpenCall(openCall === c.id ? null : c.id)} className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 text-left">
                      <span className="text-gray-500">{new Date(c.createdAt).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="font-medium text-gray-900">{c.kisi?.ad || '—'}</span>
                      <span className="rounded-full bg-gray-100 px-1.5 text-[11px] text-gray-700">{ARAMA_TUR[c.reason] || c.reason}</span>
                      <span className="text-gray-700">{c.outcome ? SONUC_AD[c.outcome.durum] || c.outcome.durum : ARAMA_DURUM[c.status] || c.status}{c.durationSec ? ` · ${Math.max(1, Math.round(c.durationSec / 60))} dk` : ''}</span>
                      {c.outcome?.memnuniyet && <span className={`rounded-full px-1.5 text-[11px] font-semibold ${c.outcome.memnuniyet === 'MEMNUN' ? 'bg-emerald-100 text-emerald-800' : c.outcome.memnuniyet === 'MEMNUN_DEGIL' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>{MEMNUNIYET_AD[c.outcome.memnuniyet]}</span>}
                      {c.outcome?.ogretmeneHaber && <span className="rounded-full bg-red-100 px-1.5 text-[11px] font-semibold text-red-800">haber verildi</span>}
                      {c.outcome?.paketIlgisi && <span className="rounded-full bg-violet-100 px-1.5 text-[11px] font-semibold text-violet-800">paket sordu</span>}
                      {(c.sms || []).map((m) => <span key={m.tur} className={`rounded-full px-1.5 text-[11px] font-semibold ${m.ok === false ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-800'}`}>{SMS_AD[m.tur] || m.tur} SMS'i {m.ok === false ? 'gitmedi' : 'gitti'}</span>)}
                    </button>
                    {c.outcome?.ozet && <p className="mt-0.5 text-gray-700">{c.outcome.ozet}</p>}
                    {c.outcome?.istek && <p className="mt-0.5 text-amber-800">İstek: {c.outcome.istek}</p>}
                    {c.error && !c.outcome && <p className="mt-0.5 text-gray-500">{c.error}</p>}
                    {openCall === c.id && c.transcript && c.transcript.length > 0 && (
                      <div className="mt-1.5 space-y-0.5 rounded bg-gray-50 p-2">
                        {c.transcript.map((t, i) => <p key={i}><b className="text-gray-600">{t.role === 'assistant' ? 'Asistan' : 'Kişi'}:</b> {t.text}</p>)}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SmtpSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isCustomSmtp, setIsCustomSmtp] = useState(false);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUserName, setSmtpUserName] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false);
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [testTo, setTestTo] = useState('');
  const accent: SettingsFormAccent = 'cyan';

  /**
   * Durum sorgusu SMTP sunucusuna CANLI bağlantı deniyor; ayarların
   * ekrana gelmesi buna bağlanmamalı. Eskiden `await loadStatus()`
   * yükleme akışının içindeydi: erişilemeyen bir SMTP sunucusunda istek
   * asılı kalınca `finally` hiç çalışmıyor, sayfa "Yükleniyor…" ekranında
   * kilitleniyordu. Artık arka planda çalışır, sonucu geldiğinde rozet
   * belirir.
   */
  const [statusLoading, setStatusLoading] = useState(true);
  const loadStatus = async () => {
    setStatusLoading(true);
    try { setStatus(await ApiService.getMailSmtpStatus()); }
    catch { setStatus(null); }
    finally { setStatusLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const d = await ApiService.getSmtpSettings();
        setIsCustomSmtp(!!d.isCustomSmtp); setSmtpHost(d.smtpHost || ''); setSmtpPort(String(d.smtpPort || 587));
        setSmtpUserName(d.smtpUserName || ''); setSmtpPasswordSet(!!d.smtpPasswordSet); setSmtpSecure(!!d.smtpSecure); setCompanyEmail(d.companyEmail || null);
      } catch (error) { showToast('SMTP ayarları yüklenirken bir hata oluştu', 'error'); }
      finally { setLoading(false); }
    })();
    void loadStatus();
  }, []);

  const handleSave = async () => {
    if (!hasUpdatePermission) return;
    if (isCustomSmtp && (!smtpHost || !smtpUserName || (!smtpPassword && !smtpPasswordSet))) { showToast('SMTP açıkken sunucu, kullanıcı adı ve parola zorunludur', 'warning'); return; }
    setSaving(true);
    try {
      await ApiService.updateSmtpSettings({ isCustomSmtp, smtpHost: smtpHost || null, smtpPort: Number(smtpPort) || 587, smtpUserName: smtpUserName || null, smtpPassword: smtpPassword || null, smtpSecure });
      setSmtpPassword(''); if (smtpPassword) setSmtpPasswordSet(true);
      showToast('SMTP ayarları kaydedildi', 'success'); void loadStatus();
    } catch (error: any) { showToast(error?.message || 'SMTP ayarları kaydedilemedi', 'error'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testTo) { showToast('Sınama için bir e-posta adresi yaz', 'warning'); return; }
    setTesting(true);
    try { await ApiService.sendMailSmtpTest(testTo); showToast(`Sınama e-postası gönderildi: ${testTo}`, 'success'); void loadStatus(); }
    catch (error: any) { showToast(error?.message || 'Sınama gönderilemedi', 'error'); }
    finally { setTesting(false); }
  };

  if (loading) return <div className='p-6 text-gray-500'>Yükleniyor…</div>;
  return (
    <div className='p-4 md:p-6 max-w-2xl space-y-4'>
      <div>
        <h2 className='text-lg font-semibold'>E-posta (SMTP)</h2>
        <p className='text-sm text-gray-600'>Kendi posta kutunuzdan gönderim (ör. bilgi@yesakademi.net). Mail Pazarlama bu ayarı kullanır. Gönderen adı <b>Firma Adı</b>, gönderen adres <b>Firma E-postası</b>{companyEmail ? ` (${companyEmail})` : ' (boş → kullanıcı adı)'} alanından gelir.</p>
      </div>
      {statusLoading
        ? <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600'>Bağlantı sınanıyor…</div>
        : status && <div className={`rounded-lg border p-3 text-sm ${status.ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
            {status.ok ? <>Bağlantı doğrulandı · {status.from} · bugün {status.sentToday}/{status.dailyLimit}</> : <>Bağlantı yok: {status.message}</>}
          </div>}
      <label className='flex items-center gap-2 text-sm'><input type='checkbox' checked={isCustomSmtp} onChange={(e) => setIsCustomSmtp(e.target.checked)} disabled={!hasUpdatePermission} />SMTP ile gönderim açık</label>
      <div className='grid md:grid-cols-2 gap-3'>
        <label className='text-sm'>Sunucu<input type='text' value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} disabled={!hasUpdatePermission} className={settingsInputClass(accent)} placeholder='mail.yesakademi.net' /></label>
        <label className='text-sm'>Port<input type='number' value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} disabled={!hasUpdatePermission} className={settingsInputClass(accent)} placeholder='587 (STARTTLS) / 465 (SSL)' /></label>
        <label className='text-sm'>Kullanıcı adı<input type='text' value={smtpUserName} onChange={(e) => setSmtpUserName(e.target.value)} disabled={!hasUpdatePermission} className={settingsInputClass(accent)} placeholder='bilgi@yesakademi.net' /></label>
        <label className='text-sm'>Parola{smtpPasswordSet && !smtpPassword ? <span className='text-xs text-gray-500'> (kayıtlı — değiştirmek için yaz)</span> : null}<input type='password' value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} disabled={!hasUpdatePermission} className={settingsInputClass(accent)} placeholder={smtpPasswordSet ? '••••••••' : ''} autoComplete='new-password' /></label>
      </div>
      <label className='flex items-center gap-2 text-sm'><input type='checkbox' checked={smtpSecure} onChange={(e) => setSmtpSecure(e.target.checked)} disabled={!hasUpdatePermission} />SSL (port 465). STARTTLS için (587) kapalı bırak.</label>
      <div className='flex flex-wrap items-center gap-2 pt-2'>
        <button onClick={handleSave} disabled={!hasUpdatePermission || saving} className='px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm disabled:opacity-60'>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
        <input type='email' value={testTo} onChange={(e) => setTestTo(e.target.value)} className={settingsInputClass(accent) + ' max-w-xs'} placeholder='sınama adresi' />
        <button onClick={handleTest} disabled={testing || !status?.configured} className='px-4 py-2 rounded-lg border text-sm disabled:opacity-60'>{testing ? 'Gönderiliyor…' : 'Sınama e-postası gönder'}</button>
      </div>
      <p className='text-xs text-gray-500'>Spam'e düşmemek için alan adında SPF, DKIM ve DMARC kayıtları olmalı (DNS tarafı). Günlük gönderim sınırı barındırıcınıza göre 200-500 arasıdır; sistem varsayılan 150/gün ile yavaş gönderir.</p>
    </div>
  );
}

function SmsSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustomSms, setIsCustomSms] = useState(false);
  const [smsApiKey, setSmsApiKey] = useState('');
  const [smsApiSecret, setSmsApiSecret] = useState('');
  const [smsSenderName, setSmsSenderName] = useState('');
  const [showApiSecret, setShowApiSecret] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const data = await ApiService.getSmsSettings();
        setIsCustomSms(data.isCustomSms || false);
        setSmsApiKey(data.smsApiKey || '');
        setSmsApiSecret(data.smsApiSecret || '');
        setSmsSenderName(data.smsSenderName || '');
      } catch (error) {
        console.error('SMS ayarları yüklenirken hata:', error);

        showToast('SMS ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    if (isCustomSms && (!smsApiKey || !smsApiSecret || !smsSenderName)) {
      showToast('SMS doğrulama aktifse tüm alanlar zorunludur', 'warning');

      return;
    };

    setSaving(true);

    try {
      await ApiService.updateSmsSettings({
        isCustomSms,
        smsProvider: 'mutlucell',
        smsApiKey: smsApiKey || null,
        smsApiSecret: smsApiSecret || null,
        smsSenderName: smsSenderName || null,
      });

      showToast('SMS ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'SMS ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'rose';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <MessageSquare className='w-6 h-6 text-rose-600' />
              SMS Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>SMS doğrulama ve gönderim ayarlarını buradan yapılandırın. Sağlayıcı: Mutlucell</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Doğrulama</span>
            </div>
            <div className='flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200'>
              <div className='flex items-center gap-3'>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCustomSms ? 'bg-rose-100 text-rose-700' : 'bg-gray-200 text-gray-500'
                  }`}>
                  <MessageSquare className='w-5 h-5' />
                </div>
                <div>
                  <p className='font-semibold text-gray-800'>SMS Doğrulama</p>
                  <p className='text-sm text-gray-500'>
                    {isCustomSms
                      ? 'Yeni kayıt olan kullanıcılardan SMS ile telefon doğrulaması istenir.'
                      : 'SMS doğrulama kapalı. Kayıt için telefon doğrulaması gerekmez.'}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => hasUpdatePermission && setIsCustomSms(!isCustomSms)}
                disabled={!hasUpdatePermission}
                className={`relative flex w-14 h-7 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 ${!hasUpdatePermission ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${isCustomSms ? 'bg-rose-600' : 'bg-gray-300'
                  }`}
                aria-pressed={isCustomSms}
              >
                <span
                  className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${isCustomSms ? 'translate-x-7' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>

          {isCustomSms && (
            <div className={SETTINGS_SECTION}>
              <div className='mb-3'>
                <span className={a.badge}>Mutlucell API</span>
              </div>
              <p className='text-sm text-gray-600 mb-6'>
                Mutlucell hesabınızdan aldığınız API bilgilerini girin. Bu bilgiler SMS gönderimi için kullanılır.
              </p>

              <div className='grid grid-cols-1 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <MessageSquare className='w-4 h-4 text-rose-600' />
                    SMS Başlığı (Originatör)
                  </label>
                  <input
                    type='text'
                    value={smsSenderName}
                    onChange={(e) => setSmsSenderName(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={settingsInputClass(accent)}
                    placeholder='Mutlucell panelinde onaylanan başlık'
                  />
                  <p className='text-xs text-gray-500 mt-1'>SMS alıcılarına görünen gönderici adı. Mutlucell panelinde onaylanmış olmalıdır.</p>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-rose-600"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    SMS Kullanıcı Adı
                  </label>
                  <input
                    type='text'
                    value={smsApiKey}
                    onChange={(e) => setSmsApiKey(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={settingsInputClass(accent)}
                    placeholder='Mutlucell kullanıcı adınız'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Lock className='w-4 h-4 text-rose-600' />
                    SMS API Key / Şifre
                  </label>
                  <div className='relative'>
                    <input
                      type={showApiSecret ? 'text' : 'password'}
                      value={smsApiSecret}
                      onChange={(e) => setSmsApiSecret(e.target.value)}
                      disabled={!hasUpdatePermission}
                      className={`${settingsInputClass(accent)} pr-10 font-mono text-sm`}
                      placeholder='Mutlucell API key veya şifreniz'
                    />
                    <button
                      type='button'
                      onClick={() => setShowApiSecret(!showApiSecret)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      {showApiSecret ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      )}
                    </button>
                  </div>
                  <p className='text-xs text-gray-500 mt-1'>Mutlucell panelinden oluşturduğunuz API key veya hesap şifreniz.</p>
                </div>
              </div>
            </div>
          )}

          {isCustomSms && (
            <div className='bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-900 shadow-sm'>
              <p className='font-semibold mb-2'>SMS doğrulama kuralları</p>
              <ul className='list-disc list-inside space-y-1 text-rose-800/90'>
                <li>Kayıt olurken kullanıcılardan telefon numarası ve SMS doğrulaması istenir</li>
                <li>Her <strong>5 dakikada bir</strong> yeni doğrulama kodu talep edilebilir</li>
                <li>Günde en fazla <strong>3 kez</strong> yeni kod gönderilebilir</li>
                <li>Kullanıcı uygulamayı kapatıp tekrar girerse <strong>eski kod geçerli</strong> kalır</li>
                <li>Doğrulama yapılmadan giriş yapılırsa tekrar SMS doğrulama ekranı gösterilir</li>
              </ul>
            </div>
          )}

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function AnnouncementSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const { settingsData, fetching, setSettingsData } = useSystemSettings();

  const [announcementCount, setAnnouncementCount] = useState<number>(3);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (settingsData) setAnnouncementCount(settingsData.announcementCount ?? 3);
  }, [settingsData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasUpdatePermission) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('announcementCount', announcementCount.toString());

      const response = await ApiService.updateSettings(formData);
      if (!response) throw new Error('Kayıt başarısız');

      setSettingsData(response);
      showToast('Duyuru ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Ayarlar güncellenirken hata:', error);

      showToast(error?.message || 'Ayarlar güncellenirken bir hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <LoadingState />;

  const accent: SettingsFormAccent = 'orange';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Bell className='w-6 h-6 text-orange-600' />
              Duyuru Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Sistemde gösterilecek son duyuru sayısını buradan yönetebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Dashboard görünümü</span>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <Bell className='w-4 h-4 text-orange-600' />
                  Gösterilecek Duyuru Sayısı
                </label>
                <input
                  type='number'
                  min='1'
                  value={announcementCount}
                  onChange={(e) => setAnnouncementCount(Number(e.target.value))}
                  disabled={!hasUpdatePermission}
                  className={settingsInputClass(accent)}
                  placeholder='Varsayılan: 3'
                />
                <p className='text-xs text-gray-500 mt-2'>
                  Kullanıcıların dashboard ekranında görebileceği maksimum &quot;son duyuru&quot; sayısını belirler (Varsayılan 3).
                </p>
              </div>
            </div>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || loading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {loading ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Save className='w-4 h-4' />
                )}
                {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function PaymentSettingsTab() {
  const { showToast } = useModal();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await ApiService.getPaymentAdminSettings();
      setData({
        ...settings,
        packages: toEditablePackages(settings.packages),
        paidDepartmentIds: settings.paidDepartmentIds || [],
      });
    } catch (error: any) {
      showToast(error?.message || 'Ödeme ayarları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // Yalnızca gerçekten girilen (boş olmayan) anahtar alanlarını gönder; boş = değiştirme.
      const providerCredentials: any = {};
      const inputs = data.providerCredentialInputs || {};
      Object.keys(inputs).forEach((provider) => {
        const clean: any = {};
        Object.entries(inputs[provider] || {}).forEach(([field, value]) => {
          if (value != null && String(value).trim() !== '') clean[field] = String(value).trim();
        });
        if (Object.keys(clean).length) providerCredentials[provider] = clean;
      });
      // Ayrı üye işyeri hesapları (ör. kafe/kiosk) — { cafe: { iyzico: { apiKey, secretKey } } }
      const providerCredentialsByAccount: any = {};
      const accountInputs = data.accountCredentialInputs || {};
      Object.keys(accountInputs).forEach((account) => {
        Object.keys(accountInputs[account] || {}).forEach((provider) => {
          const clean: any = {};
          Object.entries(accountInputs[account][provider] || {}).forEach(([field, value]) => {
            if (value != null && String(value).trim() !== '') clean[field] = String(value).trim();
          });
          if (Object.keys(clean).length) {
            providerCredentialsByAccount[account] = { ...(providerCredentialsByAccount[account] || {}), [provider]: clean };
          }
        });
      });
      const packages = toPackagePayload(data.packages);
      const updated = await ApiService.updatePaymentAdminSettings({
        paymentSystemEnabled: data.paymentSystemEnabled,
        paidModuleIds: data.paidModuleIds,
        packages,
        // Eski alan da gönderilir: arayüz backend'den ÖNCE yayına girerse eski backend fiyatları silmesin.
        packagePrices: Object.fromEntries(packages.filter((item) => item.id).map((item) => [item.id, item.price])),
        examDays: (data.examDays || []).map((item: any) => ({ date: item.date, label: String(item.label || '').trim() })),
        welcomePackageId: data.welcomePackageId || null,
        enabledProviders: data.enabledProviders,
        paidDepartmentIds: (data.paidDepartmentIds || []).filter(Boolean),
        ...(Number.isFinite(Number(data.trialSeconds)) ? { trialSeconds: Number(data.trialSeconds) } : {}),
        ...(Object.keys(providerCredentials).length ? { providerCredentials } : {}),
        ...(Object.keys(providerCredentialsByAccount).length ? { providerCredentialsByAccount } : {}),
      });
      setData({
        ...updated,
        packages: toEditablePackages(updated.packages),
        paidDepartmentIds: updated.paidDepartmentIds || [],
        providerCredentialInputs: {}, // girilen anahtarları temizle (artık maskeli 'kayıtlı' görünecek)
        accountCredentialInputs: {},
      });
      showToast('Ödeme ayarları kaydedildi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Ödeme ayarları kaydedilemedi', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <LoadingState />;

  const menuItemsById = new Map(defaultMenuItems.map((item) => [item.id, item]));

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5 flex flex-wrap items-start justify-between gap-3'>
            <div>
              <h1 className={SETTINGS_PAGE_TITLE_CLASS}><CreditCard className='h-6 w-6 text-violet-600' />Ödeme ve Ücretli Modüller</h1>
              <p className='mt-1 text-sm text-gray-600'>Paketleri, sağlayıcıları ve departman geçişlerini yönetin.</p>
              <div className='mt-3 h-1 w-20 rounded-full bg-violet-200' />
            </div>
            <button onClick={save} disabled={saving} className='inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50'>
              {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Save className='h-4 w-4' />}Kaydet
            </button>
          </div>

          <div className='space-y-5'>
            <section className={SETTINGS_SECTION}>
              <label className='flex items-center justify-between gap-4'>
                <span><span className='block font-semibold text-slate-900'>Ödeme Sistemi</span><span className='text-sm text-slate-500'>Ücretli modül akışını etkinleştirir.</span></span>
                <input type='checkbox' checked={Boolean(data.paymentSystemEnabled)} onChange={(event) => setData((prev: any) => ({ ...prev, paymentSystemEnabled: event.target.checked }))} className='h-5 w-5 accent-violet-600' />
              </label>
              <div className='mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4'>
                <span><span className='block font-semibold text-slate-900'>Deneme Süresi</span><span className='text-sm text-slate-500'>Ödemeyen öğrencinin ücretli modülde ücretsiz kullanabileceği toplam aktif süre. 0 = deneme yok (girer girmez kilit).</span></span>
                <span className='flex items-center gap-2'>
                  <input
                    type='number'
                    min={0}
                    step={60}
                    value={data.trialSeconds ?? 120}
                    onChange={(event) => setData((prev: any) => ({ ...prev, trialSeconds: event.target.value }))}
                    className='w-32 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
                  />
                  <span className='text-sm text-slate-500'>saniye</span>
                </span>
              </div>
              <div className='mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4'>
                <span className='min-w-0 flex-1'>
                  <span className='block font-semibold text-slate-900'>İlk Kayıtta Ücretsiz Paket</span>
                  <span className='text-sm text-slate-500'>
                    İlk kez kayıt olan öğrenciye (ücretli departmanda) bu paket ücretsiz ve kendiliğinden tanımlanır. Örneğin tüm bölümleri açan
                    1 günlük bir paket; satışta olması gerekmez. Yeni eklenen paket kaydedildikten sonra listede görünür.
                  </span>
                </span>
                <select
                  value={data.welcomePackageId || ''}
                  onChange={(event) => setData((prev: any) => ({ ...prev, welcomePackageId: event.target.value || null }))}
                  className='rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
                >
                  <option value=''>Kapalı</option>
                  {(data.packages || []).filter((item: any) => item.id).map((item: any) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Ücretli Modüller</h2>
              <p className='mt-1 text-sm text-slate-500'>Kullanıcıların ödeme yapmadan önce kilitli göreceği modülleri seçin.</p>
              <div className='mt-4 grid gap-3 sm:grid-cols-2'>
                {data.modules.map((module: any) => {
                  const checked = data.paidModuleIds.includes(module.id);
                  const menuItem = menuItemsById.get(module.id);
                  const ModuleIcon = menuItem?.icon || Lock;
                  const moduleColor = menuItem?.color || '#64748b';
                  return (
                    <label key={module.id} className={`group flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 transition ${checked ? 'border-violet-300 bg-violet-50 shadow-sm ring-1 ring-violet-100' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}`}>
                      <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-white shadow-sm' style={{ backgroundColor: moduleColor }}>
                        <ModuleIcon className='h-5 w-5' />
                      </span>
                      <span className='min-w-0 flex-1'>
                        <span className='block truncate text-sm font-semibold text-slate-900'>{module.label}</span>
                        <span className='block truncate text-xs text-slate-500'>{module.path}</span>
                      </span>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full border transition ${checked ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-300 bg-white text-transparent'}`}>
                        <CheckCircle2 className='h-4 w-4' />
                      </span>
                      <input type='checkbox' checked={checked} onChange={(event) => setData((prev: any) => ({
                        ...prev,
                        paidModuleIds: event.target.checked ? [...prev.paidModuleIds, module.id] : prev.paidModuleIds.filter((id: string) => id !== module.id),
                      }))} className='sr-only' />
                    </label>
                  );
                })}
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <PaymentPackagesEditor
                packages={data.packages}
                installments={data.installments || [1]}
                packageUnits={data.packageUnits || {}}
                modules={data.modules || []}
                paidModuleIds={data.paidModuleIds || []}
                onChange={(packages) => setData((prev: any) => ({ ...prev, packages }))}
              />
            </section>

            <section className={SETTINGS_SECTION}>
              <PaymentExamDaysEditor
                examDays={data.examDays || []}
                onChange={(examDays) => setData((prev: any) => ({ ...prev, examDays }))}
              />
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Ödeme Sağlayıcıları</h2>
              <p className='mt-1 text-sm text-slate-500'>Her sağlayıcının anahtarlarını girin ve etkinleştirmek için işaretleyin. Anahtarlar şifreli saklanır; kayıtlı alanlar “•••••• (kayıtlı)” görünür — değiştirmek için üzerine yazın, boş bırakırsanız korunur.</p>
              <div className='mt-4 space-y-4'>
                {data.providers.map((item: any) => {
                  const enabled = data.enabledProviders.includes(item.id);
                  const providerMeta = getPaymentProviderMeta(item.id);
                  const status = (data.providerCredentialStatus || {})[item.id] || {};
                  const spec = (data.providerFieldSpec || {})[item.id] || {};
                  const inputs = (data.providerCredentialInputs || {})[item.id] || {};
                  const configured = Boolean(status.configured ?? item.configured);
                  const setField = (field: string, value: string) => setData((prev: any) => ({
                    ...prev,
                    providerCredentialInputs: {
                      ...(prev.providerCredentialInputs || {}),
                      [item.id]: { ...((prev.providerCredentialInputs || {})[item.id] || {}), [field]: value },
                    },
                  }));
                  return (
                    <div key={item.id} className={`rounded-xl border p-4 transition ${enabled ? 'border-emerald-300 bg-emerald-50/40 shadow-sm' : 'border-slate-200 bg-white'}`}>
                      <div className='flex items-center justify-between gap-4'>
                        <span className='flex min-w-0 items-center gap-4'>
                          <span className='flex h-12 w-32 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3'>
                            <img src={providerMeta.logo} alt={providerMeta.label} className={`${providerMeta.logoClassName} max-w-full object-contain`} />
                          </span>
                          <span className='min-w-0'>
                            <span className='block font-semibold text-slate-900'>{providerMeta.label}</span>
                            <span className={`block text-xs ${configured ? 'text-emerald-600' : 'text-red-600'}`}>
                              {configured ? (status.source === 'db' ? 'Hazır — panelden girildi.' : 'Hazır — sunucu (.env) üzerinden.') : 'Zorunlu anahtarlar eksik.'}
                            </span>
                          </span>
                        </span>
                        <label className={`flex items-center gap-2 text-sm ${configured ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                          <span className='text-slate-600'>Etkin</span>
                          <input type='checkbox' disabled={!configured} checked={enabled} onChange={(event) => setData((prev: any) => ({
                            ...prev,
                            enabledProviders: event.target.checked ? [...prev.enabledProviders, item.id] : prev.enabledProviders.filter((id: string) => id !== item.id),
                          }))} className='h-5 w-5 accent-emerald-600' />
                        </label>
                      </div>
                      <div className='mt-3 grid gap-3 sm:grid-cols-2'>
                        {Object.entries(spec).map(([field, fspec]: any) => (
                          <label key={field} className='block'>
                            <span className='mb-1 block text-xs font-medium text-slate-600'>{field}{fspec.required ? ' *' : ''}</span>
                            <input
                              type={fspec.secret ? 'password' : 'text'}
                              autoComplete='new-password'
                              value={inputs[field] || ''}
                              onChange={(event) => setField(field, event.target.value)}
                              placeholder={status.fields && status.fields[field] ? '•••••• (kayıtlı)' : (fspec.required ? 'zorunlu' : 'opsiyonel')}
                              className='w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Kafe / Kiosk Ödeme Hesabı</h2>
              <p className='mt-1 text-sm text-slate-500'>
                Kafe QR ödemesi (kiosk) <strong>ayrı bir üye işyerinden</strong> tahsil edilir — paket satışından farklı tüzel kişi.
                Buraya yalnızca o hesabın anahtarlarını girin. Bu alan boşken kiosk ödemesi <strong>reddedilir</strong>;
                yukarıdaki paket hesabının anahtarlarına DÜŞMEZ — böylece kafe cirosu yanlış hesaba tahsil edilmez.
              </p>
              {(() => {
                const account = 'cafe';
                const provider = 'iyzico';
                const providerMeta = getPaymentProviderMeta(provider);
                const status = ((data.providerCredentialStatusByAccount || {})[account] || {})[provider] || {};
                const spec = (data.providerFieldSpec || {})[provider] || {};
                const inputs = ((data.accountCredentialInputs || {})[account] || {})[provider] || {};
                const configured = Boolean(status.configured);
                const setField = (field: string, value: string) => setData((prev: any) => {
                  const byAccount = prev.accountCredentialInputs || {};
                  const byProvider = byAccount[account] || {};
                  return {
                    ...prev,
                    accountCredentialInputs: {
                      ...byAccount,
                      [account]: { ...byProvider, [provider]: { ...(byProvider[provider] || {}), [field]: value } },
                    },
                  };
                });
                return (
                  <div className={`mt-4 rounded-xl border p-4 transition ${configured ? 'border-emerald-300 bg-emerald-50/40 shadow-sm' : 'border-amber-300 bg-amber-50/40'}`}>
                    <div className='flex items-center gap-4'>
                      <span className='flex h-12 w-32 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3'>
                        <img src={providerMeta.logo} alt={providerMeta.label} className={`${providerMeta.logoClassName} max-w-full object-contain`} />
                      </span>
                      <span className='min-w-0'>
                        <span className='block font-semibold text-slate-900'>Kafe hesabı — {providerMeta.label}</span>
                        <span className={`block text-xs ${configured ? 'text-emerald-600' : 'text-amber-700'}`}>
                          {configured
                            ? (status.source === 'db' ? 'Hazır — panelden girildi.' : 'Hazır — sunucu (CAFE_IYZICO_*) üzerinden.')
                            : 'Anahtarlar eksik — kiosk ödemesi şu an kapalı.'}
                        </span>
                      </span>
                    </div>
                    <div className='mt-3 grid gap-3 sm:grid-cols-2'>
                      {Object.entries(spec).map(([field, fspec]: any) => (
                        <label key={field} className='block'>
                          <span className='mb-1 block text-xs font-medium text-slate-600'>{field}{fspec.required ? ' *' : ''}</span>
                          <input
                            type={fspec.secret ? 'password' : 'text'}
                            autoComplete='new-password'
                            value={inputs[field] || ''}
                            onChange={(event) => setField(field, event.target.value)}
                            placeholder={status.fields && status.fields[field] ? '•••••• (kayıtlı)' : (fspec.required ? 'zorunlu' : 'opsiyonel')}
                            className='w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </section>

            <section className={SETTINGS_SECTION}>
              <div className='flex items-center justify-between gap-3'>
                <div><h2 className='font-semibold text-slate-900'>Ücretli Departmanlar</h2><p className='text-sm text-slate-500'>Bu departmanlardaki öğrenciler deneme süresi bitince ödeme yapana kadar ücretli modüllere erişemez. Kişi departmanında KALIR; ödeyince erişim açılır, süre bitince tekrar kapanır. (Personel/öğretmen departmanlarını eklemeyin.)</p></div>
                <button onClick={() => setData((prev: any) => ({ ...prev, paidDepartmentIds: [...(prev.paidDepartmentIds || []), ''] }))} className='inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white'><Plus className='h-3.5 w-3.5' />Departman Ekle</button>
              </div>
              <div className='mt-4 space-y-3'>
                {(data.paidDepartmentIds || []).length === 0 && (
                  <p className='rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500'>Henüz ücretli departman yok. Ödeme sistemini açmak için en az bir departman ekleyin.</p>
                )}
                {(data.paidDepartmentIds || []).map((deptId: string, index: number) => (
                  <div key={index} className='grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-[1fr_auto]'>
                    <DepartmentSelector
                      value={deptId}
                      onChange={(value) => setData((prev: any) => ({ ...prev, paidDepartmentIds: (prev.paidDepartmentIds || []).map((id: string, i: number) => i === index ? value : id) }))}
                      placeholder='Ücretli departman seçin'
                      excludeIds={(data.paidDepartmentIds || []).filter((_: string, i: number) => i !== index && _)}
                    />
                    <button onClick={() => setData((prev: any) => ({ ...prev, paidDepartmentIds: (prev.paidDepartmentIds || []).filter((_: string, i: number) => i !== index) }))} className='flex h-10 w-10 items-center justify-center rounded-lg text-red-600 hover:bg-red-50'><Trash2 className='h-4 w-4' /></button>
                  </div>
                ))}
              </div>
            </section>

            {/* Ödeme kayıtları artık ayrı "Ödemeler" ekranında (liste + nakit paket tanımlama + fatura). */}
            <section className={SETTINGS_SECTION}>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <div>
                  <h2 className='font-semibold text-slate-900'>Ödeme Kayıtları</h2>
                  <p className='text-sm text-slate-500'>Tahsilatlar, aktif paketler, nakit/ücretsiz paket tanımlama ve fatura bilgileri ayrı "Ödemeler" ekranında yönetilir.</p>
                </div>
                <a href='/payments' className='inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700'>
                  Ödemeler Ekranını Aç
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExamDatesSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [examDates, setExamDates] = useState<ExamDateItem[]>([]);
  const [pendingDepartmentIds, setPendingDepartmentIds] = useState<Record<string, string | null>>({});
  const [departmentNames, setDepartmentNames] = useState<Record<string, string>>({});
  const [allDepartments, setAllDepartments] = useState<ExamDepartmentNode[]>([]);

  const normalizeExamDate = useCallback((exam: any): ExamDateItem => {
    const departmentIds = Array.isArray(exam?.departmentIds)
      ? [...new Set((exam.departmentIds as any[]).map((id) => String(id).trim()).filter(Boolean))] as string[]
      : [];

    return {
      id: String(exam?.id || ''),
      name: typeof exam?.name === 'string' ? exam.name : '',
      date: typeof exam?.date === 'string' ? exam.date : '',
      departmentIds,
      allDepartments: departmentIds.length === 0,
    };
  }, []);

  const collapseDepartmentIdsForDisplay = useCallback((departmentIds: string[], departments: ExamDepartmentNode[]) => {
    if (!departmentIds.length || !departments.length) {
      return departmentIds;
    }

    const normalizedIds = [...new Set(departmentIds.map((id) => String(id).trim()).filter(Boolean))];
    const selectedIds = new Set(normalizedIds);
    const departmentMap = new Map(departments.map((department) => [String(department.id), department]));

    return normalizedIds.filter((departmentId) => {
      const visited = new Set<string>();
      let currentParentId = departmentMap.get(departmentId)?.parentId ? String(departmentMap.get(departmentId)?.parentId) : null;

      while (currentParentId && !visited.has(currentParentId)) {
        if (selectedIds.has(currentParentId)) {
          return false;
        }

        visited.add(currentParentId);
        const parentDepartment = departmentMap.get(currentParentId);
        currentParentId = parentDepartment?.parentId ? String(parentDepartment.parentId) : null;
      }

      return true;
    });
  }, []);

  const expandDepartmentIdsForSave = useCallback((departmentIds: string[], departments: ExamDepartmentNode[]) => {
    if (!departmentIds.length || !departments.length) {
      return [...new Set(departmentIds.map((id) => String(id).trim()).filter(Boolean))];
    }

    const normalizedIds = [...new Set(departmentIds.map((id) => String(id).trim()).filter(Boolean))];
    const childrenMap = new Map<string | null, string[]>();

    departments.forEach((department) => {
      const parentKey = department.parentId ? String(department.parentId) : null;
      const siblings = childrenMap.get(parentKey) || [];
      siblings.push(String(department.id));
      childrenMap.set(parentKey, siblings);
    });

    const expandedIds = new Set<string>();

    const addDepartmentAndChildren = (departmentId: string) => {
      if (expandedIds.has(departmentId)) {
        return;
      }

      expandedIds.add(departmentId);

      const childIds = childrenMap.get(departmentId) || [];
      childIds.forEach(addDepartmentAndChildren);
    };

    normalizedIds.forEach(addDepartmentAndChildren);

    return Array.from(expandedIds);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [examDateRes, departmentRes] = await Promise.all([
          ApiService.getExamDates(),
          ApiService.searchAllDepartments({ sortBy: 'name', sortOrder: 'ASC' }),
        ]);

        const departmentRows = (departmentRes?.data || departmentRes || [])
          .filter((department: any) => !department?.deletedAt)
          .map((department: any) => ({
            id: String(department.id),
            name: department?.name || String(department.id),
            parentId: department?.parentId ? String(department.parentId) : null,
          })) as ExamDepartmentNode[];

        setAllDepartments(departmentRows);

        const normalizedExamDates = (examDateRes.examDates || []).map((exam: any) => {
          const normalized = normalizeExamDate(exam);
          const collapsedDepartmentIds = collapseDepartmentIdsForDisplay(normalized.departmentIds, departmentRows);

          return {
            ...normalized,
            departmentIds: collapsedDepartmentIds,
            allDepartments: collapsedDepartmentIds.length === 0,
          };
        });
        setExamDates(normalizedExamDates);

        setDepartmentNames(
          Object.fromEntries(departmentRows.map((department) => [department.id, department.name]))
        );
      } catch (error) {
        console.error('Sınav tarihleri yüklenirken hata:', error);
        showToast('Sınav tarihleri yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [collapseDepartmentIdsForDisplay, normalizeExamDate, showToast]);

  const generateId = () => `exam_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  const handleAdd = () => {
    setExamDates(prev => [...prev, { id: generateId(), name: '', date: '', departmentIds: [], allDepartments: false }]);
  };

  const handleChange = (id: string, field: 'name' | 'date', value: string) => {
    setExamDates(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const handleDepartmentAdd = (examId: string, departmentId: string) => {
    if (!departmentId) return;
    setExamDates(prev => prev.map((exam) => (
      exam.id === examId
        ? {
          ...exam,
          allDepartments: false,
          departmentIds: collapseDepartmentIdsForDisplay(
            exam.departmentIds.includes(departmentId)
              ? exam.departmentIds
              : [...exam.departmentIds, departmentId],
            allDepartments
          ),
        }
        : exam
    )));
    setPendingDepartmentIds(prev => ({ ...prev, [examId]: null }));
    const selectedDepartment = allDepartments.find((department) => department.id === departmentId);
    setDepartmentNames((prev) => ({
      ...prev,
      ...(selectedDepartment?.name ? { [departmentId]: selectedDepartment.name } : {}),
    }));
  };

  const handleDepartmentRemove = (examId: string, departmentId: string) => {
    setExamDates(prev => prev.map((exam) => {
      if (exam.id !== examId) return exam;
      const nextDepartmentIds = exam.departmentIds.filter((id) => id !== departmentId);
      return {
        ...exam,
        departmentIds: nextDepartmentIds,
        allDepartments: false,
      };
    }));
  };

  const handleAllDepartmentsToggle = (examId: string, checked: boolean) => {
    setExamDates(prev => prev.map((exam) => (
      exam.id === examId
        ? {
          ...exam,
          allDepartments: checked,
          departmentIds: checked ? [] : exam.departmentIds,
        }
        : exam
    )));
    if (checked) {
      setPendingDepartmentIds(prev => ({ ...prev, [examId]: null }));
    }
  };

  const handleRemove = (id: string) => {
    setExamDates(prev => prev.filter(e => e.id !== id));
    setPendingDepartmentIds(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSave = useCallback(async () => {
    if (!hasUpdatePermission) return;
    const invalid = examDates.find((e) => !e.name.trim() || !e.date || (!e.allDepartments && e.departmentIds.length === 0));
    if (invalid) {
      showToast('Lütfen sınav adı, tarih ve departman seçimini tamamlayın', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = examDates.map((exam) => ({
        id: exam.id,
        name: exam.name.trim(),
        date: exam.date,
        departmentIds: exam.allDepartments ? [] : expandDepartmentIdsForSave(exam.departmentIds, allDepartments),
      }));
      const res = await ApiService.updateExamDates(payload);
      setExamDates((res.examDates || []).map((exam: any) => {
        const normalized = normalizeExamDate(exam);
        const collapsedDepartmentIds = collapseDepartmentIdsForDisplay(normalized.departmentIds, allDepartments);

        return {
          ...normalized,
          departmentIds: collapsedDepartmentIds,
          allDepartments: collapsedDepartmentIds.length === 0,
        };
      }));
      showToast('Sınav tarihleri başarıyla kaydedildi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Sınav tarihleri kaydedilirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  }, [allDepartments, collapseDepartmentIdsForDisplay, examDates, expandDepartmentIdsForSave, hasUpdatePermission, normalizeExamDate, showToast]);

  useEffect(() => {
    const missingDepartmentIds = [...new Set(
      examDates
        .flatMap((exam) => exam.departmentIds)
        .map((id) => String(id))
        .filter((id) => id && !departmentNames[id])
    )];

    if (missingDepartmentIds.length === 0) {
      return;
    }

    let isMounted = true;

    const resolveMissingDepartmentNames = async () => {
      const entries = await Promise.all(
        missingDepartmentIds.map(async (departmentId) => {
          try {
            const department = await ApiService.getDepartment(departmentId);
            return [departmentId, department?.name || 'Departman'] as const;
          } catch {
            return [departmentId, 'Departman'] as const;
          }
        })
      );

      if (!isMounted) {
        return;
      }

      setDepartmentNames((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    };

    void resolveMissingDepartmentNames();

    return () => {
      isMounted = false;
    };
  }, [departmentNames, examDates]);

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'fuchsia';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
                <GraduationCap className='w-6 h-6 text-fuchsia-600' />
                Sınav Tarihleri
              </h1>
              <p className='text-sm text-gray-600 mt-1'>Dashboard&apos;daki geri sayım widget&apos;ı için sınav tarihlerini buradan yönetin.</p>
              <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
            </div>
            {hasUpdatePermission && (
              <button
                type='button'
                onClick={handleAdd}
                className={`inline-flex shrink-0 items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm ${a.primaryBtn}`}
              >
                <Plus className='w-4 h-4' />
                Sınav Ekle
              </button>
            )}
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Sınav listesi</span>
            </div>
            <p className='text-sm text-gray-500 mb-4'>Eklenecek sınavların ismi, tarihi ve görünmesini istediğiniz departmanları belirtin. Dashboard widgetında sadece ilgili öğrenciler görür.</p>

          {examDates.length === 0 ? (
            <div className='text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200'>
              <GraduationCap className='w-12 h-12 text-slate-300 mx-auto mb-3' />
              <p className='text-gray-500 font-medium'>Henüz sınav eklenmemiş</p>
              <p className='text-gray-400 text-sm mt-1'>Sınav eklemek için &quot;Sınav Ekle&quot; butonunu kullanın.</p>
            </div>
          ) : (
            <div className='space-y-3'>
              {examDates.map((exam, idx) => (
                <div key={exam.id} className='flex items-start gap-3 p-3 sm:p-4 bg-slate-50/80 rounded-xl border border-slate-200 shadow-sm'>
                  <div className='flex-shrink-0 w-8 h-8 bg-fuchsia-100 rounded-xl flex items-center justify-center'>
                    <span className='text-fuchsia-700 font-bold text-sm'>{idx + 1}</span>
                  </div>
                  <div className='flex-1 space-y-3'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                      <div>
                        <label className='block text-xs font-medium text-gray-600 mb-1'>Sınav Adı</label>
                        <input
                          type='text'
                          value={exam.name}
                          onChange={(e) => handleChange(exam.id, 'name', e.target.value)}
                          disabled={!hasUpdatePermission}
                          placeholder='Ör: YKS 2025, LGS...'
                          className={`${settingsInputClass(accent)} text-sm`}
                        />
                      </div>
                      <div>
                        <label className='block text-xs font-medium text-gray-600 mb-1'>Sınav Tarihi</label>
                        <input
                          type='date'
                          value={exam.date}
                          onChange={(e) => handleChange(exam.id, 'date', e.target.value)}
                          disabled={!hasUpdatePermission}
                          className={`${settingsInputClass(accent)} text-sm`}
                        />
                      </div>
                    </div>
                    <div>
                      <div className='flex items-center justify-between gap-3 mb-2'>
                        <label className='block text-xs font-medium text-gray-600'>Görünecek Departmanlar</label>
                        <label className='inline-flex items-center gap-2 text-sm text-gray-700'>
                          <input
                            type='checkbox'
                            checked={exam.allDepartments}
                            onChange={(e) => handleAllDepartmentsToggle(exam.id, e.target.checked)}
                            disabled={!hasUpdatePermission}
                            className='rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500'
                          />
                          Tümü
                        </label>
                      </div>

                      {exam.allDepartments ? (
                        <div className='px-3 py-2 text-sm rounded-xl border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 font-medium'>
                          Bu sınav tüm departmanlara gösterilecek.
                        </div>
                      ) : (
                        <div className='space-y-3'>
                          {exam.departmentIds.length > 0 && (
                            <div className='flex flex-wrap gap-2'>
                              {exam.departmentIds.map((departmentId) => {
                                return (
                                  <span
                                    key={departmentId}
                                    className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-800 text-xs font-medium border border-fuchsia-200'
                                  >
                                    {departmentNames[departmentId] || 'Departman'}
                                    {hasUpdatePermission && (
                                      <button
                                        type='button'
                                        onClick={() => handleDepartmentRemove(exam.id, departmentId)}
                                        className='text-fuchsia-600 hover:text-fuchsia-800'
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          <DepartmentSelector
                            value={pendingDepartmentIds[exam.id] || null}
                            onChange={(departmentId) => handleDepartmentAdd(exam.id, departmentId)}
                            placeholder='Departman seçip ekleyin'
                            excludeIds={expandDepartmentIdsForSave(exam.departmentIds, allDepartments)}
                            allowClear={false}
                            required={false}
                            inline={true}
                            className='text-sm'
                            disabled={!hasUpdatePermission}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {hasUpdatePermission && (
                    <button
                      type='button'
                      onClick={() => handleRemove(exam.id)}
                      className='flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-colors'
                      title='Sınavı Sil'
                    >
                      <Trash2 className='w-4 h-4' />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 mt-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='button'
                onClick={handleSave}
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
                {saving ? 'Kaydediliyor...' : 'Sınavları Kaydet'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SystemSettingsPage() {
  const navigate = useNavigate();
  const hasListPermission = checkPermission('setting_list');

  useEffect(() => {
    if (!hasListPermission) {
      navigate('/');
    }
  }, [hasListPermission, navigate]);

  const isYesToolsDomain = isYesToolsHostname(window.location.hostname);

  const tabs: TabItemProps[] = useMemo(() => {
    const baseTabs: TabItemProps[] = [
      {
        type: 'custom',
        title: 'Genel Bilgiler',
        path: 'general',
        customComponent: <GeneralSettingsTab />
      },
      {
        type: 'custom',
        title: 'Favicon Dosyaları',
        path: 'favicons',
        customComponent: <FaviconSettingsTab />
      },
      {
        type: 'custom',
        title: 'Eğitim Ayarları',
        path: 'education',
        customComponent: <EducationSettingsTab />
      },
      {
        type: 'custom',
        title: 'Yapay Zeka Ayarları',
        path: 'ai',
        customComponent: <N8nSettingsTab />
      },
      {
        type: 'custom',
        title: 'AI Öğretmenim',
        path: 'ai-teacher',
        customComponent: <AITeacherSettingsTab />
      },
      {
        type: 'custom',
        title: 'Koç Mektupları',
        path: 'coach',
        customComponent: <CoachSettingsTab />
      },
      {
        type: 'custom',
        title: 'Telefon Asistanı',
        path: 'phone-assistant',
        customComponent: <CoachCallSettingsTab />
      },
      {
        type: 'custom',
        title: 'Duyuru Ayarları',
        path: 'announcements',
        customComponent: <AnnouncementSettingsTab />
      }
    ];

    // Ödeme ve Ücretli Modüller: tüm tenant'larda kullanılabilir (generic). Sağlayıcı anahtarları
    // panelden girilir; ödeme sistemi kapalıysa hiçbir davranışı etkilemez.
    baseTabs.splice(1, 0, {
      type: 'custom',
      title: 'Ödeme ve Ücretli Modüller',
      path: 'payments',
      customComponent: <PaymentSettingsTab />
    });

    if (isYesToolsDomain) {
      baseTabs.push({
        type: 'custom',
        title: 'Cafe Ayarları',
        path: 'cafe',
        customComponent: <CafeSettingsTab />
      });

      baseTabs.push({
        type: 'custom',
        title: 'SMS Ayarları',
        path: 'sms',
        customComponent: <SmsSettingsTab />
      });

      baseTabs.push({
        type: 'custom',
        title: 'E-posta (SMTP)',
        path: 'smtp',
        customComponent: <SmtpSettingsTab />
      });
    };

    baseTabs.push({
      type: 'custom',
      title: 'Kayıt ve Şifre Ayarları',
      path: 'registration',
      customComponent: <RegistrationSettingsTab />
    });

    baseTabs.push({
      type: 'custom',
      title: 'Sınav Tarihleri',
      path: 'exam-dates',
      customComponent: <ExamDatesSettingsTab />
    });

    return baseTabs;
  }, [isYesToolsDomain]);

  if (!hasListPermission) return null;

  return <TabPage title='Sistem Ayarları' tabs={tabs} titleClassName='text-[14px] whitespace-nowrap' tabButtonClassName='text-[12px]' />;
}
